import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ProjectTask,
  TaskGroup,
  Milestone,
  CRMUser,
  ProjectPhase,
  type DependencyEdgeType,
  type ProjectMeeting,
  type UpdateDependencyPayload,
} from '../../types';
import StatusPill from './StatusPill';
import { ChevronDown, ChevronRight, Diamond, Maximize2, Minimize2, Lock, Unlock, Undo2, FlaskConical, Pin } from 'lucide-react';
import { GanttPaymentDayCell } from '../billing/PaymentDueFlagTip';
import { GanttMeetingDayCell, GanttMeetingFlagBadge, MeetingTipContent } from './GanttMeetingDayCell';
import { dayKeyFromDate, groupMeetingsByDayKey } from '../../utils/projectMeetingsTimeline';
import {
  getProductionTaskIds,
  taskEffectiveEndDate,
  type PaymentDueFlag,
} from '../../utils/paymentDueFlags';
import { isOnboardingBridgeTaskId } from '../../utils/onboardingTaskBridge';
import {
  getMilestoneDependencyEdges,
  isMilestoneBridgeId,
  milestoneBridgeId,
  parseMilestoneBridgeId,
} from '../../utils/milestoneSchedule';
import { getDependencyEdges } from '../../utils/taskDependencies';
import {
  buildGanttFlatRows,
  buildTaskRowIndexFromFlatRows,
  isGanttTaskFlatRow,
  type GanttFlatRow,
  type StructureFilterValue,
} from '../../utils/ganttSubtaskRows';
import {
  parseGroupTimelineProjectSectionId,
  type GroupTimelineFamily,
  type GroupTimelineSection,
} from '../../utils/groupTimeline';
import {
  hasConflictIssues,
  issuesForGanttRow,
  issuesForGanttTaskRow,
  type GroupTimelineConflictBoard,
  type GroupTimelineConflictIssues,
} from '../../utils/groupTimelineConflicts';
import { unwrapSupplierDisplayTaskId } from '../../utils/groupTimelineSupplier';
import { TIMELINE_DRIFT_CAUSE_LABELS } from '../../utils/timelineDrift';
import GroupTimelineConflictBadge from './GroupTimelineConflictBadge';
import {
  GANTT_EXPANDED_GROUPS_STORAGE_KEY,
  GANTT_EXPANDED_SECTIONS_STORAGE_KEY,
  clampGanttLeftWidth,
  collapsedIdsFromExpanded,
  expandedIdsFromCollapsed,
  readStoredExpandedIdSet,
  readStoredGanttLeftWidth,
  storeExpandedIdSet,
  storeGanttLeftWidth,
  toggleIdInSet,
} from '../../utils/ganttOutlinePersist';
import {
  GANTT_COMPARE_PIN_LIMIT,
  excludePinnedGanttRows,
  ganttFlatRowPinKey,
  readStoredGanttComparePins,
  resolveGanttPinnedRows,
  storeGanttComparePins,
  toggleGanttPinKey,
} from '../../utils/ganttComparePins';
import { buildChildTasksByParent } from '../../utils/subtaskMigration';
import { rollupDatesFromChildren, rollupGroupTaskDates } from '../../utils/subtaskDateRollup';
import type { StructureRef } from '../../utils/proposalStructureApi';
import { formatSubtaskStructurePill } from '../../utils/subtaskStructures';
import './StructureSelect.css';
import GanttStructureFilter from './GanttStructureFilter';
import './GanttStructureFilter.css';
import GanttLagPopover, { type GanttLagPopoverState } from './GanttLagPopover';
import type { GanttGanttOutlineSlice, GanttSyncedViewport } from '../../utils/ganttTimelineViewSync';
import {
  buildGanttGanttOutlineSlice,
  ganttAnchorMsFromScroll,
  ganttScrollLeftForAnchor,
} from '../../utils/ganttTimelineViewSync';
import type { GanttTimelinePeerPresence } from '../../utils/ganttTimelinePresenceService';
import { hitTestGanttTimelinePointer } from '../../utils/ganttTimelineCursorUtils';
import GanttTimelinePresenceLayer from './GanttTimelinePresenceLayer';
import {
  impliedLagForNewDependencyEdge,
  inferFfLagBusinessDays,
  inferFsLagBusinessDays,
  inferSfLagBusinessDays,
  inferSsLagBusinessDays,
  isTaskLocked,
  stripTime,
} from '../../utils/taskSchedule';
import {
  fmtDay,
  ganttScheduleDebugLog,
  ganttScheduleDebugWarn,
  isGanttScheduleDebugEnabled,
} from '../../utils/ganttScheduleDebug';
import { formatGanttTaskIdForLog } from '../../utils/ganttTaskIds';
import { ganttDependencyConnectorPath } from '../../utils/ganttDependencyPaths';
import type { CommitTaskDatesHint } from '../../utils/taskDateCommit';
import type { InstallDayWeather } from '../../utils/installWeather';
import InstallWeatherRainIcon from '../weather/InstallWeatherRainIcon';
import {
  clampGanttTooltipPosition,
  GANTT_TOOLTIP_CURSOR_OFFSET,
} from '../../utils/ganttTooltipPosition';
import {
  buildGanttDays,
  computeGanttDateRangeMs,
  expandGanttDateRangeMs,
  ganttFitCellWidth,
  type GanttDateRangeMs,
  ganttFitViewportChangedEnough,
} from '../../utils/ganttDateRange';
import './GanttTimeline.css';

/** Label for link-drag HUD / tooltips (task row or milestone strip). */
function ganttLinkSourceLabel(fromId: string, tasks: ProjectTask[], milestones: Milestone[]): string {
  const t = tasks.find((x) => x.id === fromId);
  if (t?.title?.trim()) return t.title.trim();
  if (isMilestoneBridgeId(fromId)) {
    const m = milestones.find((ms) => ms.id && milestoneBridgeId(ms.id) === fromId);
    if (m?.title?.trim()) return m.title.trim();
    return 'Milestone';
  }
  return 'Task';
}

/**
 * Timeline dependency hits may reference a task row or a milestone bridge id (`milestone:<docId>`).
 * Lag inference helpers expect {@link ProjectTask}-shaped dates; synthesize from milestone target date when needed.
 */
function lagEndpointTaskShim(endpointId: string, tasks: ProjectTask[], milestones: Milestone[]): ProjectTask | undefined {
  const t = tasks.find((x) => x.id === endpointId);
  if (t) return t;
  if (!isMilestoneBridgeId(endpointId)) return undefined;
  const ms = milestones.find((m) => m.id === parseMilestoneBridgeId(endpointId));
  if (!ms?.id) return undefined;
  const d = new Date(ms.targetDate);
  const now = new Date();
  return {
    id: endpointId,
    title: ms.title?.trim() || 'Milestone',
    projectId: ms.projectId,
    department: 'preconstruction',
    assignedTo: [],
    status: ms.status === 'completed' ? 'completed' : 'pending',
    priority: 'medium',
    startDate: d,
    endDate: d,
    dependencies: [],
    progress: 0,
    position: 0,
    createdAt: now,
    updatedAt: now,
  } as ProjectTask;
}

export type GanttZoomLevel = 'day' | 'week' | 'month' | 'fit';
type ZoomLevel = GanttZoomLevel;

const ZOOM_CONFIGS: Record<Exclude<ZoomLevel, 'fit'>, { cellWidth: number; headerFormat: (d: Date) => string }> = {
  day: { cellWidth: 36, headerFormat: (d) => d.getDate().toString() },
  week: { cellWidth: 18, headerFormat: (d) => `W${Math.ceil(d.getDate() / 7)}` },
  month: { cellWidth: 6, headerFormat: (d) => d.getDate().toString() },
};

/** Dynamic column width when zoom = fit (computed from viewport ÷ day count). */
const FIT_CELL_MIN = 4;
const FIT_CELL_MAX = 120;
/** Preset / Fit button zoom transition (ms); resize + Ctrl+wheel stay instant for low latency. */
const ZOOM_ANIM_MS = 170;

interface GanttTimelineProps {
  tasks: ProjectTask[];
  groups: TaskGroup[];
  milestones: Milestone[];
  /** Client proposal / sales order or engagement letter signature date (shown on Preconstruction row). */
  proposalSignedAt?: Date | null;
  /** Current project phase (shown on task tooltips). */
  projectPhase?: ProjectPhase | null;
  teamMembers: CRMUser[];
  onUpdateTask: (id: string, updates: Partial<ProjectTask>) => void;
  /** When set, drag commits once on mouseup. Pass `{ preferRipple: true }` when Shift is held (skip dependency prompt — cascade dependents). */
  onCommitTaskDates?: (taskId: string, startDate: Date, endDate: Date, hint: CommitTaskDatesHint) => void | Promise<void>;
  /**
   * Task bars expose **Finish** (right) and **Start** (left) ports for creating links only as **finish-to-start**:
   * drag from this task’s **Finish** to another task → that task succeeds this one after its finish,
   * or drag from **Start** backward to another task → that task is the predecessor on a finish-to-start link.
   * Use the lag popover / task panel to switch to SS / FF / SF when needed.
   */
  onCreateFsDependency?: (
    predecessorId: string,
    successorId: string,
    linkType?: DependencyEdgeType,
  ) => void | Promise<void>;
  /** Edit lag + link type for an existing dependency; then reschedule. */
  onUpdateDependencyLag?: (
    successorId: string,
    predecessorId: string,
    payload: UpdateDependencyPayload,
  ) => void | Promise<void>;
  /** Remove FS link from successor → predecessor; then reschedule. */
  onRemoveDependency?: (successorId: string, predecessorId: string) => void | Promise<void>;
  onSelectTask: (task: ProjectTask) => void;
  /**
   * Toggle a task's `scheduleLocked` flag. Locked tasks cannot be moved by the scheduler
   * or by direct drag; the only way to move them is to unlock first (or, for completed
   * tasks, edit their actuals).
   */
  onToggleTaskLock?: (taskId: string, next: boolean) => void | Promise<void>;
  /** When false (default), manual critical-path chain overlay is not drawn. */
  showCriticalPathOverlay?: boolean;
  /** Tagged prior positions for Timber Lead Time / Cut Timber Package (admin drift overlay). */
  timelineDriftGhosts?: import('../../utils/timelineDrift').TimelineDriftGhost[];
  /** Hide the task name sidebar; timeline uses full width (e.g. wall display). */
  hideLeftPanel?: boolean;
  /** Read-only: no drag, resize, link creation, or milestone move. */
  displayMode?: boolean;
  /**
   * When false, the left-panel status pill stays editable even in displayMode
   * (supplier hub: status only, not schedule). Defaults to displayMode.
   */
  statusReadOnly?: boolean;
  /** Initial zoom when mounted (e.g. `fit` for TV). Preset buttons still work. */
  initialZoom?: GanttZoomLevel;
  /** Reverts the last timeline schedule write after this timeline unmount (Project Hub clears history on project switch). */
  onTimelineUndo?: () => void | Promise<void>;
  timelineUndoCanUndo?: boolean;
  timelineUndoBusy?: boolean;
  /** Installation-task weather from project site forecast (10-day horizon). */
  getInstallTaskRangeRainyDays?: (start: Date, end: Date) => InstallDayWeather[];
  formatInstallDayWeatherLine?: (day: InstallDayWeather) => string;
  /**
   * Payment-due forecast flags (read-only markers in the milestone strip).
   * They move only because their anchor milestone moves — never draggable.
   */
  paymentFlags?: PaymentDueFlag[];
  /** Calendar meetings for this project (day-header markers). Filtered by Advanced → Show meetings. */
  projectMeetings?: ProjectMeeting[];
  /** Proposal structures for subtask filter chips. */
  dealStructures?: StructureRef[];
  structureFilter?: Set<StructureFilterValue> | null;
  onStructureFilterChange?: (filter: Set<StructureFilterValue> | null) => void;
  /** Override bar color (cross-project group timeline only). */
  resolveTaskBarColor?: (task: ProjectTask) => string;
  /** Early-funnel projects staff selected for the under-contract planning overlay. */
  simulatedProjectIds?: readonly string[];
  /** All-projects timeline: collapsible Procurement / Production / Install bands. */
  timelineSections?: GroupTimelineSection[];
  /** All-projects Gantt: collapse-first + pin strip. Single-project stays expanded. */
  allProjectsMode?: boolean;
  /** Resets the expand-only timeline range when switching project or group timeline. */
  ganttRangeResetKey?: string;
  outlineApiRef?: React.MutableRefObject<GanttAllProjectsApi | null>;
  onPinnedCountChange?: (count: number) => void;
  /** All-projects only: same-supplier / install overlaps and missing-supplier warnings. */
  conflictBoard?: GroupTimelineConflictBoard | null;
  conflictFamily?: GroupTimelineFamily;
  onConflictBadgeClick?: (issues: GroupTimelineConflictIssues) => void;
  /** Fired when collapse / expand / pin outline changes (timeline collaboration). */
  onGanttOutlineChange?: (outline: GanttGanttOutlineSlice) => void;
  /** Zoom and scroll changes for live sync and local split. */
  onViewportChange?: (viewport: GanttSyncedViewport) => void;
  /** Live cursors + pointer sampling when staff timeline collaboration is active. */
  ganttTimelinePresence?: {
    selfViewHash: string;
    selfSyncId?: string | null;
    peers: GanttTimelinePeerPresence[];
    onCursorSample: (sample: {
      rowKey: string | null;
      timeMs: number | null;
      pane: 'chart' | 'left';
    }) => void;
    onCursorClear: () => void;
  };
}

export type GanttAllProjectsApi = {
  collapseAll: () => void;
  expandAll: () => void;
  clearPins: () => void;
  getOutlineState: () => GanttGanttOutlineSlice;
  applyOutlineState: (outline: GanttGanttOutlineSlice) => void;
  getViewport: () => GanttSyncedViewport;
  applyViewport: (viewport: GanttSyncedViewport) => void;
};

function undoHotkeyTypingTarget(active: Element | null): boolean {
  if (!active || !(active instanceof HTMLElement)) return false;
  const tag = active.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (active.isContentEditable) return true;
  return !!active.closest('[role="textbox"], [role="combobox"], [role="searchbox"]');
}

const ROW_HEIGHT = 36;
/** Matches `.pm-gantt-bar` vertical layout (`top: 6`, `height: ROW_HEIGHT − 12`). Splines terminate at mid-bar, not row edges. */
const GANTT_BAR_TOP_PAD = 6;
const GANTT_BAR_BODY_HEIGHT = ROW_HEIGHT - 12;

function ganttTaskBarCenterY(rowIdx: number): number {
  return rowIdx * ROW_HEIGHT + GANTT_BAR_TOP_PAD + GANTT_BAR_BODY_HEIGHT / 2;
}

/** Spline attachment inside task bar edges (rounded rect + aligns with Start/Finish link rails). Narrow bars fall back to center. */
function ganttBarAttachX(leftEdgePx: number, rightEdgeExclusivePx: number, anchor: 'start' | 'finish', cellWidth: number): number {
  const inset = Math.min(5, Math.max(1, cellWidth * 0.13));
  const span = rightEdgeExclusivePx - leftEdgePx;
  if (span <= inset * 2 + 1) return (leftEdgePx + rightEdgeExclusivePx) / 2;
  return anchor === 'start' ? leftEdgePx + inset : rightEdgeExclusivePx - inset;
}

/** Horizontal spread when multiple milestones fall on the same day (single row). */
const MILESTONE_SAME_DAY_SPREAD_PX = 18;
/** Task → milestone: spline ends this far above the task bar top; arrow + strip hint replace a line into the diamond. */
const MILESTONE_TASK_DEP_STEM_GAP_PX = 16;
/** Small “target” dot offset below strip center (under the main diamond). */
const MILESTONE_DEP_HINT_BELOW_CENTER_PX = 9;
const MILESTONE_DEP_HINT_R = 3;
/** Pixels from task bar start/end to reveal FS dependency lines. */
const DEP_EDGE_REVEAL_PX = 28;
const TOOLTIP_DELAY_MS = 500;
/** Bar / FS-link drag: scroll timeline / task list when pointer nears pane edges */
const LINK_DRAG_AUTOSCROLL_EDGE = 52;
const LINK_DRAG_AUTOSCROLL_MAX = 18;

function depEdgeMatchesReveal(
  predId: string,
  succId: string,
  edgeType: DependencyEdgeType,
  reveal: { taskId: string; end: 'start' | 'finish' } | null,
): boolean {
  if (!reveal) return false;
  switch (edgeType) {
    case 'SS':
      return (
        (reveal.taskId === predId && reveal.end === 'start') ||
        (reveal.taskId === succId && reveal.end === 'start')
      );
    case 'FS':
      if (reveal.taskId === predId && reveal.end === 'finish') return true;
      if (reveal.taskId === succId && reveal.end === 'start') return true;
      return false;
    case 'FF':
      return (
        (reveal.taskId === predId && reveal.end === 'finish') ||
        (reveal.taskId === succId && reveal.end === 'finish')
      );
    case 'SF':
      if (reveal.taskId === predId && reveal.end === 'start') return true;
      if (reveal.taskId === succId && reveal.end === 'finish') return true;
      return false;
    default:
      return false;
  }
}

function isMovingToDependencyOverlay(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest?.('.pm-gantt-arrows--overlay');
}

const STRIP_MILESTONE_CY = ROW_HEIGHT / 2;

/** Min calendar-day span and min bar width (px) before showing initials + duration on open task bars. */
const GANTT_OPEN_META_MIN_DAY_SPAN = 3;
const GANTT_OPEN_META_MIN_BAR_WIDTH = 80;

function initialsFromDisplayName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] ?? '';
    const b = parts[parts.length - 1][0] ?? '';
    return (a + b).toUpperCase();
  }
  if (parts.length === 1 && parts[0].length) return parts[0].slice(0, 2).toUpperCase();
  return '';
}

function taskAssigneeInitials(task: ProjectTask, members: CRMUser[]): string {
  const ids = task.assignedTo || [];
  if (ids.length === 0) return '';
  const parts = ids
    .map((id) => {
      const u = members.find((m) => m.uid === id);
      const name = u?.displayName || id;
      return initialsFromDisplayName(name);
    })
    .filter(Boolean);
  return parts.slice(0, 4).join('·');
}

function taskCalendarDaySpan(start: Date, end: Date): number {
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
}

const fmtEnum = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const phaseDisplay = (p?: ProjectPhase | null) =>
  p === 'preconstruction' ? 'Preconstruction' : p === 'contracted' ? 'Under Contract' : null;

const GanttTimeline: React.FC<GanttTimelineProps> = ({
  tasks, groups, milestones, proposalSignedAt, teamMembers, onUpdateTask, onCommitTaskDates, onCreateFsDependency,
  onUpdateDependencyLag,
  onRemoveDependency,
  onSelectTask,
  onToggleTaskLock,
  projectPhase,
  showCriticalPathOverlay = false,
  timelineDriftGhosts = [],
  hideLeftPanel = false,
  displayMode = false,
  statusReadOnly = displayMode,
  initialZoom,
  onTimelineUndo,
  timelineUndoCanUndo = false,
  timelineUndoBusy = false,
  getInstallTaskRangeRainyDays,
  formatInstallDayWeatherLine,
  paymentFlags,
  projectMeetings = [],
  dealStructures = [],
  structureFilter = null,
  onStructureFilterChange,
  resolveTaskBarColor,
  simulatedProjectIds,
  timelineSections,
  allProjectsMode = false,
  ganttRangeResetKey = '',
  outlineApiRef,
  onPinnedCountChange,
  conflictBoard,
  conflictFamily = 'all',
  onConflictBadgeClick,
  onGanttOutlineChange,
  onViewportChange,
  ganttTimelinePresence,
}) => {
  const simulatedProjectIdSet = useMemo(
    () => new Set(simulatedProjectIds ?? []),
    [simulatedProjectIds],
  );
  const isSimulatedProjectRow = useCallback(
    (projectId?: string | null) => !!projectId && simulatedProjectIdSet.has(projectId),
    [simulatedProjectIdSet],
  );
  const multiProjectTimeline = useMemo(() => {
    const ids = new Set(tasks.map((t) => t.projectId).filter(Boolean));
    return ids.size > 1;
  }, [tasks]);
  const projectSectionTimeline = useMemo(
    () => (timelineSections ?? []).some((s) => !!parseGroupTimelineProjectSectionId(s.id)),
    [timelineSections],
  );
  const [zoom, setZoom] = useState<ZoomLevel>(initialZoom ?? 'day');
  const [fitCellWidth, setFitCellWidth] = useState(36);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [expandedParentIds, setExpandedParentIds] = useState<Set<string>>(new Set());
  const [pinKeys, setPinKeys] = useState<string[]>(() =>
    allProjectsMode ? readStoredGanttComparePins() : [],
  );
  const [leftPanelWidth, setLeftPanelWidth] = useState(readStoredGanttLeftWidth);
  const [resizingLeftPanel, setResizingLeftPanel] = useState(false);
  const leftPanelWidthRef = useRef(leftPanelWidth);
  leftPanelWidthRef.current = leftPanelWidth;
  const leftResizeDragRef = useRef<{ startX: number; startW: number } | null>(null);
  const [statusMenuTaskId, setStatusMenuTaskId] = useState<string | null>(null);
  const ganttRootRef = useRef<HTMLDivElement>(null);
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const leftBodyScrollRef = useRef<HTMLDivElement>(null);
  const ganttContainerRef = useRef<HTMLDivElement>(null);
  const [presenceScrollTick, setPresenceScrollTick] = useState(0);
  const lastPresenceCursorSentRef = useRef(0);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const pinRightScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingVerticalScrollRef = useRef(false);
  const [dragging, setDragging] = useState<{
    taskId: string;
    type: 'move' | 'resize-end' | 'resize-start';
    startX: number;
    origStart: Date;
    origEnd: Date;
  } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ taskId: string; start: Date; end: Date } | null>(null);
  const dragPreviewRef = useRef<{ taskId: string; start: Date; end: Date } | null>(null);
  const linkDragRef = useRef<{
    fromId: string;
    linkEnd: 'finish' | 'start';
    x: number;
    y: number;
    curX: number;
    curY: number;
  } | null>(null);
  const [linkDrag, setLinkDrag] = useState<{
    fromId: string;
    linkEnd: 'finish' | 'start';
    x: number;
    y: number;
    curX: number;
    curY: number;
  } | null>(null);
  const lastLinkPointerRef = useRef({ x: 0, y: 0 });
  const linkDragAutoScrollRafRef = useRef<number | null>(null);
  /** Bar/milestone drag: last pointer + scrollLeft at drag start (for edge auto-pan). */
  const lastBarPointerRef = useRef({ x: 0, y: 0 });
  const barDragScrollOriginRef = useRef(0);
  const barDragAutoScrollRafRef = useRef<number | null>(null);
  const suppressSelectUntilRef = useRef(0);
  /** True after pointer moved during an active bar drag — suppress click-to-open even if dates snap back unchanged */
  const dragPointerMovedRef = useRef(false);
  /** Updated on move/up; commit uses mouseup’s Shift so you can press Shift mid-drag. */
  const dragRippleRef = useRef(false);
  const tooltipTimerRef = useRef<number | null>(null);
  /** Gantt task tooltip: dep ports should not open the rich tooltip; re-open after pointer leaves a port. */
  const armTaskTooltipAfterLinkRef = useRef(false);
  const armMilestoneTooltipAfterLinkRef = useRef(false);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: React.ReactNode } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [lagPopover, setLagPopover] = useState<GanttLagPopoverState | null>(null);
  const lagPopoverRef = useRef<HTMLDivElement>(null);

  /** When set, FS edges touching this task (start/finish) and link ports at that end are emphasized; others stay faint. */
  const [depEdgeReveal, setDepEdgeReveal] = useState<{
    taskId: string;
    end: 'start' | 'finish';
  } | null>(null);

  /** True while the timeline is actively scrolling — skip Fit-mode width recals from overlay scrollbars. */
  const timelineScrollingRef = useRef(false);
  const timelineScrollIdleRef = useRef<number | null>(null);
  /** Dedupe console warnings when a dependency can’t be drawn (predecessor missing from `tasks` / row). */
  const warnedSkippedDepEdgeRef = useRef<Set<string>>(new Set());

  /** Animated horizontal column width (preserves smooth zoom; distinct from `fitCellWidth` state while easing). */
  const [smoothCellWidth, setSmoothCellWidth] = useState(ZOOM_CONFIGS.day.cellWidth);
  const smoothCellWidthRef = useRef(ZOOM_CONFIGS.day.cellWidth);
  const zoomAnimRafRef = useRef<number | null>(null);
  const suppressViewportPublishRef = useRef(false);
  const pendingRemoteViewportRef = useRef<GanttSyncedViewport | null>(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  /** When set, Fit must not restyle columns from this pane's own width. */
  const externalColumnLockRef = useRef(false);
  const [viewportApplyTick, setViewportApplyTick] = useState(0);
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  const viewportEmitRef = useRef<(() => void) | null>(null);
  /**
   * After Ctrl/Cmd+wheel timeline zoom (fit mode column resize), restores scrollLeft so the
   * content pixel under the pointer stays steady (standard map-style zoom-at-cursor behavior).
   */
  const timelineWheelZoomAnchorRef = useRef<{ viewportX: number; ratio: number; prevScrollLeft: number } | null>(
    null,
  );
  /** While zoom ≠ fit, tracks `days.length` so switching to fit does not reset smooth width; when length changes while fit, we re-sync. */
  const fitDaysLenForSyncRef = useRef(0);
  /** Auto-scroll to today once per zoom preset — not again when the axis grows from live data. */
  const todayScrollForZoomRef = useRef<ZoomLevel | null>(null);

  const hideTooltip = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    setTooltip(null);
  }, []);

  const cancelZoomAnim = useCallback(() => {
    if (zoomAnimRafRef.current != null) {
      cancelAnimationFrame(zoomAnimRafRef.current);
      zoomAnimRafRef.current = null;
    }
  }, []);

  const animateCellWidthTo = useCallback(
    (to: number, duration = ZOOM_ANIM_MS) => {
      cancelZoomAnim();
      const from = smoothCellWidthRef.current;
      const clamped = Math.max(FIT_CELL_MIN, Math.min(FIT_CELL_MAX, to));
      if (Math.abs(from - clamped) < 0.4) {
        smoothCellWidthRef.current = clamped;
        setSmoothCellWidth(clamped);
        return;
      }
      const t0 = performance.now();
      const step = (now: number) => {
        const u = Math.min(1, (now - t0) / duration);
        const eased = 1 - (1 - u) ** 3;
        const v = from + (clamped - from) * eased;
        smoothCellWidthRef.current = v;
        setSmoothCellWidth(v);
        if (u < 1) {
          zoomAnimRafRef.current = requestAnimationFrame(step);
        } else {
          zoomAnimRafRef.current = null;
          smoothCellWidthRef.current = clamped;
          setSmoothCellWidth(clamped);
        }
      };
      zoomAnimRafRef.current = requestAnimationFrame(step);
    },
    [cancelZoomAnim],
  );

  const updateDepEdgeRevealFromBarPointer = useCallback(
    (e: React.MouseEvent<HTMLElement>, taskId: string) => {
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const w = r.width;
      if (w <= DEP_EDGE_REVEAL_PX * 2) {
        setDepEdgeReveal(x < w / 2 ? { taskId, end: 'start' } : { taskId, end: 'finish' });
      } else if (x <= DEP_EDGE_REVEAL_PX) {
        setDepEdgeReveal({ taskId, end: 'start' });
      } else if (x >= w - DEP_EDGE_REVEAL_PX) {
        setDepEdgeReveal({ taskId, end: 'finish' });
      } else {
        setDepEdgeReveal(null);
      }
    },
    [],
  );

  const scheduleTooltip = useCallback((clientX: number, clientY: number, content: React.ReactNode) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    tooltipTimerRef.current = window.setTimeout(() => {
      setTooltip({ x: clientX, y: clientY, content });
      tooltipTimerRef.current = null;
    }, TOOLTIP_DELAY_MS);
  }, []);

  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !tooltip) return;
    const { left, top } = clampGanttTooltipPosition({
      cursorX: tooltip.x,
      cursorY: tooltip.y,
      width: el.offsetWidth,
      height: el.offsetHeight,
      viewportW: window.innerWidth,
      viewportH: window.innerHeight,
    });
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [tooltip]);

  useEffect(() => () => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
  }, []);

  useEffect(() => () => {
    if (timelineScrollIdleRef.current != null) {
      window.clearTimeout(timelineScrollIdleRef.current);
    }
  }, []);

  useEffect(() => () => {
    if (linkDragAutoScrollRafRef.current != null) {
      window.cancelAnimationFrame(linkDragAutoScrollRafRef.current);
    }
  }, []);

  useEffect(() => {
    if (!lagPopover) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLagPopover(null);
    };
    const onDown = (e: MouseEvent) => {
      const el = lagPopoverRef.current;
      if (el && !el.contains(e.target as Node)) setLagPopover(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [lagPopover]);

  useEffect(() => {
    if (dragging) hideTooltip();
  }, [dragging, hideTooltip]);

  const cellWidth = smoothCellWidth;
  const showDayNumbersInHeader = zoom === 'day' || (zoom === 'fit' && cellWidth >= 20);

  const computedRange = useMemo(() => {
    const taskTimes: unknown[] = [];
    tasks.forEach((t) => {
      if (t.startDate) taskTimes.push(t.startDate);
      if (t.endDate) taskTimes.push(t.endDate);
    });
    const extraTimes: unknown[] = [];
    if (proposalSignedAt) extraTimes.push(proposalSignedAt);
    (paymentFlags || []).forEach((f) => {
      if (f.dueDate) extraTimes.push(f.dueDate);
    });
    (projectMeetings || []).forEach((m) => {
      if (m.start) extraTimes.push(m.start);
    });
    timelineDriftGhosts.forEach((g) => {
      extraTimes.push(g.start, g.end);
    });
    return computeGanttDateRangeMs({
      taskTimes,
      milestoneTimes: milestones.map((m) => m.targetDate),
      extraTimes,
    });
  }, [tasks, milestones, proposalSignedAt, paymentFlags, projectMeetings, timelineDriftGhosts]);

  const expandedRangeRef = useRef<GanttDateRangeMs | null>(null);
  const expandedRangeScopeRef = useRef(ganttRangeResetKey);
  if (expandedRangeScopeRef.current !== ganttRangeResetKey) {
    expandedRangeScopeRef.current = ganttRangeResetKey;
    expandedRangeRef.current = null;
  }
  if (expandedRangeRef.current == null) {
    expandedRangeRef.current = computedRange;
  } else {
    expandedRangeRef.current = expandGanttDateRangeMs(expandedRangeRef.current, computedRange);
  }
  const rangeBounds =
    expandedRangeRef.current.hasAnchors || computedRange.hasAnchors
      ? expandedRangeRef.current
      : computedRange;
  const rangeStartMsRef = useRef(rangeBounds.startMs);
  rangeStartMsRef.current = rangeBounds.startMs;

  const days = useMemo(
    () => buildGanttDays(rangeBounds.startMs, rangeBounds.endMs),
    [rangeBounds.startMs, rangeBounds.endMs],
  );

  const months = useMemo(() => {
    const result: { startIdx: number; days: number }[] = [];
    let prevKey = '';
    days.forEach((d, i) => {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key !== prevKey) {
        result.push({
          startIdx: i,
          days: 1,
        });
        prevKey = key;
      } else {
        result[result.length - 1].days++;
      }
    });
    return result;
  }, [days]);

  const todayIdx = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return days.findIndex(d => d.getTime() === now.getTime());
  }, [days]);

  const handleZoomToFit = useCallback(() => {
    externalColumnLockRef.current = false;
    const el = scrollRef.current;
    if (!el || days.length === 0) return;
    const w = el.clientWidth;
    const cw = ganttFitCellWidth(w, days.length, FIT_CELL_MIN, FIT_CELL_MAX);
    setFitCellWidth(cw);
    setZoom('fit');
    animateCellWidthTo(cw);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollLeft = 0;
    });
  }, [days.length, animateCellWidthTo]);

  const handleToggleGanttFullscreen = useCallback(async () => {
    const root = ganttRootRef.current;
    if (!root) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
      webkitExitFullscreen?: () => Promise<void>;
    };
    const elWebkit = root as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
    const isFs =
      document.fullscreenElement === root || doc.webkitFullscreenElement === root;
    try {
      if (isFs) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else await doc.webkitExitFullscreen?.();
      } else if (root.requestFullscreen) {
        await root.requestFullscreen();
      } else if (elWebkit.webkitRequestFullscreen) {
        await Promise.resolve(elWebkit.webkitRequestFullscreen());
      }
    } catch {
      /* blocked or unsupported */
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      const root = ganttRootRef.current;
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      setFullscreenActive(
        root != null &&
          (document.fullscreenElement === root || doc.webkitFullscreenElement === root),
      );
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const initialFitAppliedRef = useRef(false);
  useEffect(() => {
    if (initialZoom !== 'fit' || initialFitAppliedRef.current || days.length === 0) return;
    initialFitAppliedRef.current = true;
    const id = requestAnimationFrame(() => handleZoomToFit());
    return () => cancelAnimationFrame(id);
  }, [initialZoom, days.length, handleZoomToFit]);

  useEffect(() => {
    if (zoom === 'fit') {
      todayScrollForZoomRef.current = 'fit';
      return;
    }
    const el = scrollRef.current;
    if (!el || todayIdx < 0) return;
    if (todayScrollForZoomRef.current === zoom) return;
    todayScrollForZoomRef.current = zoom;
    // Use preset target column width — not animated `smoothCellWidth` — so we don’t re-run every
    // frame of `animateCellWidthTo` (which wrongly pinned scroll toward “today”).
    const cw = ZOOM_CONFIGS[zoom].cellWidth;
    const scrollTo = todayIdx * cw - el.clientWidth / 3;
    el.scrollLeft = Math.max(0, scrollTo);
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = el.scrollLeft;
    if (pinRightScrollRef.current) pinRightScrollRef.current.scrollLeft = el.scrollLeft;
  }, [todayIdx, zoom]);

  /** Fit mode: when the day count changes, recompute width (instant). Entering fit does not run here so preset/Fit/wheel animations are not cancelled. */
  useEffect(() => {
    if (zoom !== 'fit' || externalColumnLockRef.current) {
      fitDaysLenForSyncRef.current = days.length;
      return;
    }
    if (fitDaysLenForSyncRef.current === days.length) return;
    const prevDayCount = fitDaysLenForSyncRef.current;
    fitDaysLenForSyncRef.current = days.length;
    const el = scrollRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const n = Math.max(1, days.length);
    const cwIdeal = ganttFitCellWidth(w, n, FIT_CELL_MIN, FIT_CELL_MAX);
    // When the axis grows (task edit / payment anchors), keep zoom — don’t auto-compress columns.
    const cw =
      prevDayCount > 0 && n > prevDayCount
        ? Math.max(cwIdeal, smoothCellWidthRef.current)
        : cwIdeal;
    if (Math.abs(cw - smoothCellWidthRef.current) < 0.5) return;
    setFitCellWidth(cw);
    cancelZoomAnim();
    smoothCellWidthRef.current = cw;
    setSmoothCellWidth(cw);
  }, [zoom, days.length, cancelZoomAnim]);

  /** Fit mode: viewport width changes — snap column width (no tween). */
  useEffect(() => {
    if (zoom !== 'fit') return;
    const el = scrollRef.current;
    if (!el) return;
    let lastW = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (timelineScrollingRef.current || externalColumnLockRef.current) return;
      const w = el.clientWidth;
      if (!ganttFitViewportChangedEnough(lastW, w)) return;
      lastW = w;
      const n = Math.max(1, days.length);
      const cw = ganttFitCellWidth(w, n, FIT_CELL_MIN, FIT_CELL_MAX);
      if (Math.abs(cw - smoothCellWidthRef.current) < 0.5) return;
      setFitCellWidth(cw);
      cancelZoomAnim();
      smoothCellWidthRef.current = cw;
      setSmoothCellWidth(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom, days.length, cancelZoomAnim]);

  useEffect(() => {
    if (zoom === 'fit' || externalColumnLockRef.current) return;
    animateCellWidthTo(ZOOM_CONFIGS[zoom].cellWidth);
  }, [zoom, animateCellWidthTo]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      // Trackpad pinch arrives as ctrl+wheel; ignore when the gesture is mostly a pan.
      if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
      e.preventDefault();
      externalColumnLockRef.current = false;
      const n = Math.max(1, days.length);
      if (n === 0) return;
      const factor = e.deltaY > 0 ? 0.97 : 1.03;
      const base = smoothCellWidthRef.current;
      const next = Math.max(FIT_CELL_MIN, Math.min(FIT_CELL_MAX, base * factor));
      if (Math.abs(next - base) < 1e-6) return;

      const rect = el.getBoundingClientRect();
      const viewportX = Math.max(0, Math.min(el.clientWidth, e.clientX - rect.left));
      const ratio = next / base;
      timelineWheelZoomAnchorRef.current = {
        viewportX,
        ratio,
        prevScrollLeft: el.scrollLeft,
      };

      setZoom('fit');
      setFitCellWidth(next);
      cancelZoomAnim();
      smoothCellWidthRef.current = next;
      setSmoothCellWidth(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [days.length, cancelZoomAnim]);

  /** Apply scroll anchoring once the timeline grid reflects the new column width after wheel zoom. */
  useLayoutEffect(() => {
    const anchor = timelineWheelZoomAnchorRef.current;
    if (!anchor) return;
    timelineWheelZoomAnchorRef.current = null;

    const el = scrollRef.current;
    if (!el) return;
    const n = Math.max(1, days.length);
    const cw = smoothCellWidthRef.current;
    const maxScroll = Math.max(0, n * cw - el.clientWidth);
    const { viewportX: vx, ratio, prevScrollLeft } = anchor;
    const nextScroll = prevScrollLeft * ratio + vx * (ratio - 1);
    el.scrollLeft = Math.max(0, Math.min(maxScroll, nextScroll));

    const head = headerScrollRef.current;
    if (head) head.scrollLeft = el.scrollLeft;
    if (pinRightScrollRef.current) pinRightScrollRef.current.scrollLeft = el.scrollLeft;
  }, [smoothCellWidth, days.length]);

  useEffect(
    () => () => {
      if (zoomAnimRafRef.current != null) cancelAnimationFrame(zoomAnimRafRef.current);
    },
    [],
  );

  /** Global shortcut while Timeline view is mounted; ignores fields / editors so panel typing keeps browser undo. */
  useEffect(() => {
    if (displayMode || !onTimelineUndo) return;

    const onKey = (ev: KeyboardEvent) => {
      if (!((ev.ctrlKey || ev.metaKey) && ev.key === 'z') || ev.shiftKey) return;
      if (!timelineUndoCanUndo || timelineUndoBusy) return;
      if (undoHotkeyTypingTarget(document.activeElement)) return;
      ev.preventDefault();
      void onTimelineUndo();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayMode, onTimelineUndo, timelineUndoCanUndo, timelineUndoBusy]);

  const undoShortcutHint =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent ?? '') ? '⌘Z' : 'Ctrl+Z';

  const handleTimelineScroll = useCallback(() => {
    if (!scrollRef.current) return;
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
    if (pinRightScrollRef.current) {
      pinRightScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
    if (!isSyncingVerticalScrollRef.current && leftBodyScrollRef.current) {
      isSyncingVerticalScrollRef.current = true;
      leftBodyScrollRef.current.scrollTop = scrollRef.current.scrollTop;
      isSyncingVerticalScrollRef.current = false;
    }
    timelineScrollingRef.current = true;
    if (timelineScrollIdleRef.current != null) {
      window.clearTimeout(timelineScrollIdleRef.current);
    }
    timelineScrollIdleRef.current = window.setTimeout(() => {
      timelineScrollIdleRef.current = null;
      timelineScrollingRef.current = false;
    }, 160);
    hideTooltip();
    if (ganttTimelinePresence) setPresenceScrollTick((n) => n + 1);
    viewportEmitRef.current?.();
  }, [hideTooltip, ganttTimelinePresence]);

  const handleLeftBodyScroll = useCallback(() => {
    if (!leftBodyScrollRef.current || !scrollRef.current) return;
    if (isSyncingVerticalScrollRef.current) return;
    isSyncingVerticalScrollRef.current = true;
    scrollRef.current.scrollTop = leftBodyScrollRef.current.scrollTop;
    isSyncingVerticalScrollRef.current = false;
    if (ganttTimelinePresence) setPresenceScrollTick((n) => n + 1);
    viewportEmitRef.current?.();
  }, [ganttTimelinePresence]);

  /** While dragging a bar or FS connector, pan scroll containers so off-screen dates/rows stay reachable. */
  const applyLinkDragAutoScroll = useCallback((clientX: number, clientY: number) => {
    const E = LINK_DRAG_AUTOSCROLL_EDGE;
    const MAX = LINK_DRAG_AUTOSCROLL_MAX;

    const syncHeaderToBody = () => {
      const body = scrollRef.current;
      if (!body || !headerScrollRef.current) return;
      headerScrollRef.current.scrollLeft = body.scrollLeft;
      if (pinRightScrollRef.current) pinRightScrollRef.current.scrollLeft = body.scrollLeft;
    };

    const syncLeftToBodyVertical = () => {
      const body = scrollRef.current;
      const left = leftBodyScrollRef.current;
      if (!body || !left) return;
      if (isSyncingVerticalScrollRef.current) return;
      isSyncingVerticalScrollRef.current = true;
      left.scrollTop = body.scrollTop;
      isSyncingVerticalScrollRef.current = false;
    };

    const syncBodyToLeftVertical = () => {
      const body = scrollRef.current;
      const left = leftBodyScrollRef.current;
      if (!body || !left) return;
      if (isSyncingVerticalScrollRef.current) return;
      isSyncingVerticalScrollRef.current = true;
      body.scrollTop = left.scrollTop;
      isSyncingVerticalScrollRef.current = false;
    };

    const body = scrollRef.current;
    if (body) {
      const r = body.getBoundingClientRect();
      const rowBand = clientY >= r.top - 72 && clientY <= r.bottom + 72;
      const colBand = clientX >= r.left - 100 && clientX <= r.right + 100;

      if (rowBand) {
        let d = 0;
        if (clientX < r.left + E) {
          const depth = Math.min(E, r.left + E - clientX);
          const t = depth / E;
          d = -Math.max(1, Math.round(MAX * t));
        } else if (clientX > r.right - E) {
          const depth = Math.min(E, clientX - (r.right - E));
          const t = depth / E;
          d = Math.max(1, Math.round(MAX * t));
        }
        if (d !== 0) {
          const maxL = Math.max(0, body.scrollWidth - body.clientWidth);
          body.scrollLeft = Math.max(0, Math.min(maxL, body.scrollLeft + d));
          syncHeaderToBody();
        }
      }

      if (colBand) {
        let d = 0;
        if (clientY < r.top + E) {
          const depth = Math.min(E, r.top + E - clientY);
          const t = depth / E;
          d = -Math.max(1, Math.round(MAX * t));
        } else if (clientY > r.bottom - E) {
          const depth = Math.min(E, clientY - (r.bottom - E));
          const t = depth / E;
          d = Math.max(1, Math.round(MAX * t));
        }
        if (d !== 0) {
          const maxT = Math.max(0, body.scrollHeight - body.clientHeight);
          body.scrollTop = Math.max(0, Math.min(maxT, body.scrollTop + d));
          syncLeftToBodyVertical();
        }
      }
    }

    const left = leftBodyScrollRef.current;
    if (left) {
      const r = left.getBoundingClientRect();
      const inLeft = clientX >= r.left - 24 && clientX <= r.right + 24;
      if (inLeft) {
        let d = 0;
        if (clientY < r.top + E) {
          const depth = Math.min(E, r.top + E - clientY);
          const t = depth / E;
          d = -Math.max(1, Math.round(MAX * t));
        } else if (clientY > r.bottom - E) {
          const depth = Math.min(E, clientY - (r.bottom - E));
          const t = depth / E;
          d = Math.max(1, Math.round(MAX * t));
        }
        if (d !== 0) {
          const maxT = Math.max(0, left.scrollHeight - left.clientHeight);
          left.scrollTop = Math.max(0, Math.min(maxT, left.scrollTop + d));
          syncBodyToLeftVertical();
        }
      }
    }
  }, []);

  const dayIndex = useCallback((d: Date) => {
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    return Math.round((target.getTime() - rangeBounds.startMs) / 86400000);
  }, [rangeBounds.startMs]);

  const tasksByGroup = useMemo(() => {
    const map = new Map<string, ProjectTask[]>();
    groups.forEach(g => map.set(g.id!, []));
    const ungrouped: ProjectTask[] = [];
    tasks.forEach(t => {
      if (t.parentTaskId) return;
      const gid = t.groupId || '';
      if (map.has(gid)) map.get(gid)!.push(t);
      else ungrouped.push(t);
    });
    if (ungrouped.length > 0) map.set('__ungrouped', ungrouped);
    map.forEach(arr => arr.sort((a, b) => (a.position || 0) - (b.position || 0)));
    return map;
  }, [tasks, groups]);

  const allSectionIds = useMemo(
    () => (timelineSections ?? []).map((s) => s.id).filter(Boolean),
    [timelineSections],
  );
  const allGroupIds = useMemo(
    () => groups.map((g) => g.id).filter((id): id is string => !!id),
    [groups],
  );
  const outlineIdKey = `${allSectionIds.join('|')}::${allGroupIds.join('|')}`;

  const persistSectionCollapse = useCallback((collapsed: Set<string>) => {
    if (!allProjectsMode) return;
    storeExpandedIdSet(
      GANTT_EXPANDED_SECTIONS_STORAGE_KEY,
      expandedIdsFromCollapsed(allSectionIds, collapsed),
    );
  }, [allProjectsMode, allSectionIds]);

  const persistGroupCollapse = useCallback((collapsed: Set<string>) => {
    if (!allProjectsMode) return;
    storeExpandedIdSet(
      GANTT_EXPANDED_GROUPS_STORAGE_KEY,
      expandedIdsFromCollapsed(allGroupIds, collapsed),
    );
  }, [allProjectsMode, allGroupIds]);

  const beginLeftPanelResize = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    leftResizeDragRef.current = { startX: e.clientX, startW: leftPanelWidthRef.current };
    setResizingLeftPanel(true);
  }, []);

  useEffect(() => {
    if (!resizingLeftPanel) return;
    const onMove = (e: PointerEvent) => {
      const drag = leftResizeDragRef.current;
      if (!drag) return;
      const next = clampGanttLeftWidth(drag.startW + (e.clientX - drag.startX));
      leftPanelWidthRef.current = next;
      setLeftPanelWidth(next);
    };
    const onUp = () => {
      leftResizeDragRef.current = null;
      setResizingLeftPanel(false);
      storeGanttLeftWidth(leftPanelWidthRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [resizingLeftPanel]);

  const collapseSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = toggleIdInSet(prev, sectionId);
      persistSectionCollapse(next);
      return next;
    });
  }, [persistSectionCollapse]);

  const collapseGroup = useCallback((groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = toggleIdInSet(prev, groupId);
      persistGroupCollapse(next);
      return next;
    });
  }, [persistGroupCollapse]);

  const collapseAllOutline = useCallback(() => {
    const nextSections = new Set(allSectionIds);
    const nextGroups = new Set(allGroupIds);
    setCollapsedSections(nextSections);
    setCollapsedGroups(nextGroups);
    persistSectionCollapse(nextSections);
    persistGroupCollapse(nextGroups);
  }, [allSectionIds, allGroupIds, persistSectionCollapse, persistGroupCollapse]);

  const expandAllOutline = useCallback(() => {
    const empty = new Set<string>();
    setCollapsedSections(empty);
    setCollapsedGroups(empty);
    persistSectionCollapse(empty);
    persistGroupCollapse(empty);
  }, [persistSectionCollapse, persistGroupCollapse]);

  useLayoutEffect(() => {
    if (!allProjectsMode) {
      setCollapsedSections(new Set());
      setCollapsedGroups(new Set());
      return;
    }
    setCollapsedSections(
      collapsedIdsFromExpanded(
        allSectionIds,
        readStoredExpandedIdSet(GANTT_EXPANDED_SECTIONS_STORAGE_KEY),
      ),
    );
    setCollapsedGroups(
      collapsedIdsFromExpanded(
        allGroupIds,
        readStoredExpandedIdSet(GANTT_EXPANDED_GROUPS_STORAGE_KEY),
      ),
    );
  }, [allProjectsMode, outlineIdKey, allSectionIds, allGroupIds]);

  const sourceRows = useMemo(
    () =>
      buildGanttFlatRows({
        groups,
        tasks,
        collapsedGroups,
        expandedParentIds,
        structureFilter,
        sections: timelineSections,
        collapsedSections,
      }),
    [groups, tasks, collapsedGroups, expandedParentIds, structureFilter, timelineSections, collapsedSections],
  );

  const childTasksByParent = useMemo(() => buildChildTasksByParent(tasks), [tasks]);

  const childCountByParent = useMemo(() => {
    const map = new Map<string, number>();
    childTasksByParent.forEach((kids, parentId) => map.set(parentId, kids.length));
    return map;
  }, [childTasksByParent]);

  const pinKeySet = useMemo(() => new Set(pinKeys), [pinKeys]);

  const pinResolution = useMemo(
    () =>
      allProjectsMode
        ? resolveGanttPinnedRows({
          pinKeys,
          visibleRows: sourceRows,
          tasks,
          groups,
          sections: timelineSections,
          childCountByParent,
        })
        : { rows: [] as GanttFlatRow[], keptKeys: [] as string[] },
    [allProjectsMode, pinKeys, sourceRows, tasks, groups, timelineSections, childCountByParent],
  );

  const pinnedRows = pinResolution.rows;

  useEffect(() => {
    if (!allProjectsMode) return;
    if (pinResolution.keptKeys.length === pinKeys.length
      && pinResolution.keptKeys.every((k, i) => k === pinKeys[i])) {
      return;
    }
    setPinKeys(pinResolution.keptKeys);
    storeGanttComparePins(pinResolution.keptKeys);
  }, [allProjectsMode, pinKeys, pinResolution.keptKeys]);

  const flatRows = useMemo(
    () => (allProjectsMode ? excludePinnedGanttRows(sourceRows, pinKeySet) : sourceRows),
    [allProjectsMode, sourceRows, pinKeySet],
  );

  const togglePinKey = useCallback((key: string) => {
    setPinKeys((prev) => {
      const next = toggleGanttPinKey(prev, key);
      storeGanttComparePins(next.keys);
      return next.keys;
    });
  }, []);

  const clearPins = useCallback(() => {
    setPinKeys([]);
    storeGanttComparePins([]);
  }, []);

  const getOutlineState = useCallback(
    (): GanttGanttOutlineSlice =>
      buildGanttGanttOutlineSlice({
        collapsedGroups,
        collapsedSections,
        expandedParentIds,
        pinKeys,
      }),
    [collapsedGroups, collapsedSections, expandedParentIds, pinKeys],
  );

  const applyOutlineState = useCallback(
    (outline: GanttGanttOutlineSlice) => {
      const nextGroups = new Set(outline.collapsedGroups);
      const nextSections = new Set(outline.collapsedSections);
      const nextExpanded = new Set(outline.expandedParentIds);
      setCollapsedGroups(nextGroups);
      setCollapsedSections(nextSections);
      setExpandedParentIds(nextExpanded);
      persistGroupCollapse(nextGroups);
      persistSectionCollapse(nextSections);
      const nextPins = [...outline.pinKeys];
      setPinKeys(nextPins);
      storeGanttComparePins(nextPins);
    },
    [persistGroupCollapse, persistSectionCollapse],
  );

  const writeSyncedScroll = useCallback((scrollLeft: number, scrollTop: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    isSyncingVerticalScrollRef.current = true;
    el.scrollLeft = Math.max(0, Math.min(maxLeft, scrollLeft));
    el.scrollTop = Math.max(0, Math.min(maxTop, scrollTop));
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = el.scrollLeft;
    if (pinRightScrollRef.current) pinRightScrollRef.current.scrollLeft = el.scrollLeft;
    if (leftBodyScrollRef.current) leftBodyScrollRef.current.scrollTop = el.scrollTop;
    isSyncingVerticalScrollRef.current = false;
  }, []);

  const getViewport = useCallback((): GanttSyncedViewport => {
    const el = scrollRef.current;
    const cw = smoothCellWidthRef.current || cellWidth || 1;
    return {
      zoom,
      anchorMs: ganttAnchorMsFromScroll(rangeBounds.startMs, el?.scrollLeft ?? 0, cw),
      scrollTop: el?.scrollTop ?? 0,
      columnWidth: cw,
    };
  }, [zoom, rangeBounds.startMs, cellWidth]);

  const applyViewport = useCallback((viewport: GanttSyncedViewport) => {
    const nextWidth = viewport.columnWidth && viewport.columnWidth > 0
      ? viewport.columnWidth
      : viewport.zoom === 'fit'
        ? smoothCellWidthRef.current
        : ZOOM_CONFIGS[viewport.zoom].cellWidth;
    if (Math.abs(smoothCellWidthRef.current - nextWidth) > 0.5) {
      externalColumnLockRef.current = true;
      cancelZoomAnim();
      smoothCellWidthRef.current = nextWidth;
      setSmoothCellWidth(nextWidth);
      setFitCellWidth(nextWidth);
    }
    if (viewport.zoom !== zoomRef.current) {
      externalColumnLockRef.current = true;
      suppressViewportPublishRef.current = true;
      pendingRemoteViewportRef.current = viewport;
      todayScrollForZoomRef.current = viewport.zoom;
      setZoom(viewport.zoom);
      setViewportApplyTick((n) => n + 1);
      return;
    }
    todayScrollForZoomRef.current = viewport.zoom;
    suppressViewportPublishRef.current = true;
    const scrollLeft = ganttScrollLeftForAnchor(rangeStartMsRef.current, viewport.anchorMs, nextWidth);
    writeSyncedScroll(scrollLeft, viewport.scrollTop);
    suppressViewportPublishRef.current = false;
  }, [writeSyncedScroll, cancelZoomAnim]);

  viewportEmitRef.current = () => {
    if (suppressViewportPublishRef.current) return;
    onViewportChangeRef.current?.(getViewport());
  };

  useLayoutEffect(() => {
    const pending = pendingRemoteViewportRef.current;
    if (!pending) return;
    const el = scrollRef.current;
    if (!el) return;
    const targetWidth = pending.columnWidth && pending.columnWidth > 0
      ? pending.columnWidth
      : pending.zoom === 'fit'
        ? smoothCellWidthRef.current
        : ZOOM_CONFIGS[pending.zoom].cellWidth;
    if (Math.abs(smoothCellWidthRef.current - targetWidth) > 1.5) return;
    const scrollLeft = ganttScrollLeftForAnchor(rangeBounds.startMs, pending.anchorMs, targetWidth);
    writeSyncedScroll(scrollLeft, pending.scrollTop);
    pendingRemoteViewportRef.current = null;
    onViewportChangeRef.current?.(getViewport());
    suppressViewportPublishRef.current = false;
  }, [zoom, smoothCellWidth, rangeBounds.startMs, writeSyncedScroll, viewportApplyTick, getViewport]);

  useEffect(() => {
    if (suppressViewportPublishRef.current) return;
    viewportEmitRef.current?.();
  }, [zoom, smoothCellWidth]);

  useEffect(() => {
    onPinnedCountChange?.(allProjectsMode ? pinKeys.length : 0);
  }, [allProjectsMode, pinKeys.length, onPinnedCountChange]);

  useEffect(() => {
    onGanttOutlineChange?.(getOutlineState());
  }, [onGanttOutlineChange, getOutlineState]);

  useEffect(() => {
    if (!outlineApiRef) return;
    outlineApiRef.current = {
      collapseAll: collapseAllOutline,
      expandAll: expandAllOutline,
      clearPins,
      getOutlineState,
      applyOutlineState,
      getViewport,
      applyViewport,
    };
    return () => {
      outlineApiRef.current = null;
    };
  }, [
    outlineApiRef,
    collapseAllOutline,
    expandAllOutline,
    clearPins,
    getOutlineState,
    applyOutlineState,
    getViewport,
    applyViewport,
  ]);

  const handleGanttPresenceMove = useCallback(
    (e: React.PointerEvent, pane: 'chart' | 'left') => {
      if (!ganttTimelinePresence) return;
      const now = Date.now();
      if (now - lastPresenceCursorSentRef.current < 150) return;
      const body = pane === 'chart' ? scrollRef.current : leftBodyScrollRef.current;
      if (!body) return;
      const rect = body.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const contentX =
        pane === 'chart'
          ? (scrollRef.current?.scrollLeft ?? 0) + (e.clientX - rect.left)
          : 0;
      const contentY = body.scrollTop + (e.clientY - rect.top);
      const hit = hitTestGanttTimelinePointer({
        flatRows,
        rangeStartMs: rangeBounds.startMs,
        cellWidth,
        contentX,
        contentY,
      });
      lastPresenceCursorSentRef.current = now;
      ganttTimelinePresence.onCursorSample({
        rowKey: hit.rowKey,
        timeMs: pane === 'chart' ? hit.timeMs : null,
        pane,
      });
    },
    [ganttTimelinePresence, flatRows, rangeBounds.startMs, cellWidth],
  );

  const handleGanttPresenceLeave = useCallback(() => {
    ganttTimelinePresence?.onCursorClear();
  }, [ganttTimelinePresence]);

  const presenceProjection = useMemo(() => {
    void presenceScrollTick;
    return {
      flatRows,
      rangeStartMs: rangeBounds.startMs,
      cellWidth,
      scrollLeft: scrollRef.current?.scrollLeft ?? 0,
      scrollTop: scrollRef.current?.scrollTop ?? 0,
      leftPanelWidth,
      hideLeftPanel,
      pinnedRowCount: pinnedRows.length,
    };
  }, [
    presenceScrollTick,
    flatRows,
    rangeBounds.startMs,
    cellWidth,
    leftPanelWidth,
    hideLeftPanel,
    pinnedRows.length,
  ]);

  const taskRowIndexExpanded = useMemo(
    () => buildTaskRowIndexFromFlatRows(flatRows),
    [flatRows],
  );

  /** Production task ids + latest committed end — lets production-anchored payment flags ride a live drag. */
  const productionAnchorInfo = useMemo(() => {
    const projectId = tasks[0]?.projectId;
    if (!projectId) return null;
    const taskIds = getProductionTaskIds(projectId, tasks, groups);
    if (taskIds.size === 0) return null;
    let baseEnd: Date | null = null;
    for (const t of tasks) {
      if (!t.id || !taskIds.has(t.id)) continue;
      const end = taskEffectiveEndDate(t);
      if (end && (!baseEnd || end.getTime() > baseEnd.getTime())) baseEnd = end;
    }
    return { taskIds, baseEnd };
  }, [tasks, groups]);

  /**
   * Payment flags with a resolvable date; anchored flags follow their
   * milestone's drag preview, and production-anchored flags (40%) shift with
   * the live drag of any Production-group task. Drag shifts are calendar-day
   * deltas — exact business-day lag is recomputed on commit.
   */
  const visiblePaymentFlags = useMemo(() => {
    if (!paymentFlags || paymentFlags.length === 0) return [];

    let productionDragDeltaMs = 0;
    if (
      dragPreview &&
      productionAnchorInfo?.baseEnd &&
      productionAnchorInfo.taskIds.has(dragPreview.taskId)
    ) {
      let previewEnd: Date | null = null;
      for (const t of tasks) {
        if (!t.id || !productionAnchorInfo.taskIds.has(t.id)) continue;
        const end =
          t.id === dragPreview.taskId ? dragPreview.end : taskEffectiveEndDate(t);
        if (end && (!previewEnd || end.getTime() > previewEnd.getTime())) previewEnd = end;
      }
      if (previewEnd) {
        productionDragDeltaMs = previewEnd.getTime() - productionAnchorInfo.baseEnd.getTime();
      }
    }

    return paymentFlags
      .map(f => {
        let effectiveDate = f.dueDate;
        if (f.anchorMilestoneId && dragPreview?.taskId === milestoneBridgeId(f.anchorMilestoneId)) {
          effectiveDate = dragPreview.start;
        } else if (f.anchoredToProductionTasks && effectiveDate && productionDragDeltaMs !== 0) {
          effectiveDate = new Date(effectiveDate.getTime() + productionDragDeltaMs);
        }
        return { flag: f, effectiveDate };
      })
      .filter((e): e is { flag: PaymentDueFlag; effectiveDate: Date } => !!e.effectiveDate);
  }, [paymentFlags, dragPreview, productionAnchorInfo, tasks]);

  const meetingsByDayKey = useMemo(
    () => groupMeetingsByDayKey(projectMeetings),
    [projectMeetings],
  );

  /** Payments grouped by timeline day index for inline day-header markers. */
  const paymentFlagsByDayIndex = useMemo(() => {
    const map = new Map<number, Array<{ flag: PaymentDueFlag; effectiveDate: Date }>>();
    for (const entry of visiblePaymentFlags) {
      const idx = Math.max(0, Math.min(dayIndex(entry.effectiveDate), days.length - 1));
      const list = map.get(idx) ?? [];
      list.push(entry);
      map.set(idx, list);
    }
    return map;
  }, [visiblePaymentFlags, dayIndex, days.length]);

  /** Horizontal offsets in the strip when multiple items share a calendar day (milestones + proposal signed). */
  const { milestoneOffsetById, proposalStripOffsetPx } = useMemo(() => {
    const mMap = new Map<string, number>();
    let proposalOff = 0;
    type StripEntry =
      | { kind: 'milestone'; id: string; dayIdx: number }
      | { kind: 'proposal'; dayIdx: number };
    const entries: StripEntry[] = [];
    milestones.forEach((ms) => {
      if (!ms.id) return;
      const bridge = milestoneBridgeId(ms.id);
      const eff =
        bridge && dragPreview?.taskId === bridge
          ? dragPreview.start
          : new Date(ms.targetDate);
      const raw = dayIndex(eff);
      const dayIdx = Math.max(0, Math.min(raw, days.length - 1));
      entries.push({ kind: 'milestone', id: ms.id, dayIdx });
    });
    if (proposalSignedAt) {
      const raw = dayIndex(new Date(proposalSignedAt));
      const dayIdx = Math.max(0, Math.min(raw, days.length - 1));
      entries.push({ kind: 'proposal', dayIdx });
    }
    const byDay = new Map<number, StripEntry[]>();
    entries.forEach((e) => {
      const g = byDay.get(e.dayIdx) ?? [];
      g.push(e);
      byDay.set(e.dayIdx, g);
    });
    byDay.forEach((group) => {
      group.forEach((e, pos) => {
        const off =
          group.length <= 1 ? 0 : (pos - (group.length - 1) / 2) * MILESTONE_SAME_DAY_SPREAD_PX;
        if (e.kind === 'milestone') mMap.set(e.id, off);
        else proposalOff = off;
      });
    });
    return { milestoneOffsetById: mMap, proposalStripOffsetPx: proposalOff };
  }, [milestones, proposalSignedAt, dragPreview, days.length, dayIndex]);

  const milestoneAnchorX = useCallback(
    (ms: Milestone) => {
      if (!ms.id) return 0;
      const bridge = milestoneBridgeId(ms.id);
      const eff =
        dragPreview?.taskId === bridge ? dragPreview.start : new Date(ms.targetDate);
      const rawIdx = dayIndex(eff);
      const idx = Math.max(0, Math.min(rawIdx, days.length - 1));
      const off = milestoneOffsetById.get(ms.id) ?? 0;
      return idx * cellWidth + cellWidth / 2 + off;
    },
    [cellWidth, dayIndex, days.length, dragPreview, milestoneOffsetById],
  );

  const timelineContentHeight = flatRows.length * ROW_HEIGHT;

  const totalWidth = days.length * cellWidth;

  const handleMouseDown = (e: React.MouseEvent, taskId: string, type: 'move' | 'resize-end' | 'resize-start') => {
    if (displayMode) return;
    e.preventDefault();
    e.stopPropagation();
    dragPointerMovedRef.current = false;
    dragRippleRef.current = e.shiftKey;
    if (isOnboardingBridgeTaskId(taskId) && !onCommitTaskDates) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const origStart = new Date(task.startDate);
    const origEnd = new Date(task.endDate);
    const init = { taskId, start: origStart, end: origEnd };
    dragPreviewRef.current = init;
    setDragPreview(init);
    lastBarPointerRef.current = { x: e.clientX, y: e.clientY };
    barDragScrollOriginRef.current = scrollRef.current?.scrollLeft ?? 0;
    setDragging({ taskId, type, startX: e.clientX, origStart, origEnd });
  };

  /** Milestones are points in time (diamonds), not duration bars — drag horizontally to change target date. */
  const handleMilestoneMoveMouseDown = (e: React.MouseEvent, bridge: string, targetDate: Date) => {
    if (displayMode) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    e.stopPropagation();
    dragPointerMovedRef.current = false;
    hideTooltip();
    if (!onCommitTaskDates) return;
    dragRippleRef.current = e.shiftKey;
    const d = new Date(targetDate);
    const init = { taskId: bridge, start: d, end: new Date(d) };
    dragPreviewRef.current = init;
    setDragPreview(init);
    lastBarPointerRef.current = { x: e.clientX, y: e.clientY };
    barDragScrollOriginRef.current = scrollRef.current?.scrollLeft ?? 0;
    setDragging({ taskId: bridge, type: 'move', startX: e.clientX, origStart: d, origEnd: new Date(d) });
  };

  useEffect(() => {
    if (!dragging) return;

    const applyPreviewFromPointer = (clientX: number) => {
      const scrollDelta = (scrollRef.current?.scrollLeft ?? 0) - barDragScrollOriginRef.current;
      const dx = clientX - dragging.startX + scrollDelta;
      if (Math.abs(dx) > 4) dragPointerMovedRef.current = true;
      const daysDelta = Math.round(dx / cellWidth);

      if (dragging.type === 'move') {
        const newStart = new Date(dragging.origStart);
        newStart.setDate(newStart.getDate() + daysDelta);
        const newEnd = new Date(dragging.origEnd);
        newEnd.setDate(newEnd.getDate() + daysDelta);
        const next = { taskId: dragging.taskId, start: newStart, end: newEnd };
        dragPreviewRef.current = next;
        setDragPreview(next);
      } else if (dragging.type === 'resize-start') {
        const newStart = new Date(dragging.origStart);
        newStart.setDate(newStart.getDate() + daysDelta);
        const endT = stripTime(dragging.origEnd).getTime();
        const nsT = stripTime(newStart).getTime();
        if (nsT > endT) {
          newStart.setTime(dragging.origEnd.getTime());
        }
        const next = { taskId: dragging.taskId, start: newStart, end: dragging.origEnd };
        dragPreviewRef.current = next;
        setDragPreview(next);
      } else {
        const newEnd = new Date(dragging.origEnd);
        newEnd.setDate(newEnd.getDate() + daysDelta);
        if (stripTime(newEnd).getTime() >= stripTime(dragging.origStart).getTime()) {
          const next = { taskId: dragging.taskId, start: dragging.origStart, end: newEnd };
          dragPreviewRef.current = next;
          setDragPreview(next);
        }
      }
    };

    const handleMove = (e: MouseEvent) => {
      dragRippleRef.current = e.shiftKey;
      lastBarPointerRef.current = { x: e.clientX, y: e.clientY };
      applyPreviewFromPointer(e.clientX);
    };

    /** Edge auto-pan: keep shifting the calendar while the pointer sits near a margin. */
    const runAutoScrollLoop = () => {
      barDragAutoScrollRafRef.current = null;
      const before = scrollRef.current?.scrollLeft ?? 0;
      applyLinkDragAutoScroll(lastBarPointerRef.current.x, lastBarPointerRef.current.y);
      const after = scrollRef.current?.scrollLeft ?? 0;
      if (after !== before) {
        applyPreviewFromPointer(lastBarPointerRef.current.x);
      }
      barDragAutoScrollRafRef.current = window.requestAnimationFrame(runAutoScrollLoop);
    };
    barDragAutoScrollRafRef.current = window.requestAnimationFrame(runAutoScrollLoop);

    const handleUp = (e: MouseEvent) => {
      if (barDragAutoScrollRafRef.current != null) {
        window.cancelAnimationFrame(barDragAutoScrollRafRef.current);
        barDragAutoScrollRafRef.current = null;
      }
      const preferRipple = e.shiftKey;
      const preview = dragPreviewRef.current;
      if (preview && preview.taskId === dragging.taskId) {
        const os = stripTime(dragging.origStart).getTime();
        const oe = stripTime(dragging.origEnd).getTime();
        const ps = stripTime(preview.start).getTime();
        const pe = stripTime(preview.end).getTime();
        const datesMoved = os !== ps || oe !== pe;
        if (datesMoved || dragPointerMovedRef.current) {
          suppressSelectUntilRef.current = Date.now() + 450;
        }
        if (datesMoved) {
          if (onCommitTaskDates) {
            if (isGanttScheduleDebugEnabled()) {
              ganttScheduleDebugLog('timeline bar drag → commit', {
                taskId: preview.taskId,
                preferRipple,
                dragType: dragging.type,
                from: { start: fmtDay(dragging.origStart), end: fmtDay(dragging.origEnd) },
                to: { start: fmtDay(preview.start), end: fmtDay(preview.end) },
              });
            }
            void onCommitTaskDates(preview.taskId, preview.start, preview.end, {
              preferRipple,
            });
          } else {
            onUpdateTask(preview.taskId, { startDate: preview.start, endDate: preview.end });
          }
        }
      }
      dragPreviewRef.current = null;
      setDragPreview(null);
      setDragging(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      if (barDragAutoScrollRafRef.current != null) {
        window.cancelAnimationFrame(barDragAutoScrollRafRef.current);
        barDragAutoScrollRafRef.current = null;
      }
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, cellWidth, onUpdateTask, onCommitTaskDates, applyLinkDragAutoScroll]);

  const handleLinkMouseDown = useCallback(
    (e: React.MouseEvent, taskId: string, linkEnd: 'finish' | 'start') => {
      if (displayMode) return;
      e.preventDefault();
      e.stopPropagation();
      hideTooltip();
      if (!onCreateFsDependency) {
        ganttScheduleDebugWarn('link drag ignored: onCreateFsDependency not provided (hub props)');
        return;
      }
      const ld = {
        fromId: taskId,
        linkEnd,
        x: e.clientX,
        y: e.clientY,
        curX: e.clientX,
        curY: e.clientY,
      };
      linkDragRef.current = ld;
      setLinkDrag({ ...ld });
      lastLinkPointerRef.current = { x: e.clientX, y: e.clientY };

      const runAutoScrollLoop = () => {
        linkDragAutoScrollRafRef.current = null;
        if (!linkDragRef.current) return;
        applyLinkDragAutoScroll(lastLinkPointerRef.current.x, lastLinkPointerRef.current.y);
        linkDragAutoScrollRafRef.current = window.requestAnimationFrame(runAutoScrollLoop);
      };
      linkDragAutoScrollRafRef.current = window.requestAnimationFrame(runAutoScrollLoop);

      const move = (ev: MouseEvent) => {
        lastLinkPointerRef.current = { x: ev.clientX, y: ev.clientY };
        ld.curX = ev.clientX;
        ld.curY = ev.clientY;
        setLinkDrag({ ...ld });
      };
      const up = (ev: MouseEvent) => {
        if (linkDragAutoScrollRafRef.current != null) {
          window.cancelAnimationFrame(linkDragAutoScrollRafRef.current);
          linkDragAutoScrollRafRef.current = null;
        }
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        const stack = document.elementsFromPoint(ev.clientX, ev.clientY);
        /** Dependency line hit-strokes sit above bars; ignore them so the task under the line wins. */
        const isDepLineHitOverlay = (n: HTMLElement | SVGElement) => n.classList.contains('pm-gantt-arrow-hit');
        /** First resolved task row under cursor (may be Start / Finish ports or bar surface). */
        let toId: string | undefined;
        for (const n of stack) {
          if (!(n instanceof HTMLElement || n instanceof SVGElement)) continue;
          if (isDepLineHitOverlay(n)) continue;
          const host = n.closest('[data-gantt-task-id]') as HTMLElement | null;
          const id = host?.dataset.ganttTaskId;
          if (id && id !== ld.fromId) {
            toId = id;
            break;
          }
        }
        if (toId && toId !== ld.fromId) {
          suppressSelectUntilRef.current = Date.now() + 400;
          /** Drag from Finish: always FS. Drag from Start: backward onto predecessor → FS. */
          if (ld.linkEnd === 'finish') {
            if (isGanttScheduleDebugEnabled()) {
              ganttScheduleDebugLog('link drag drop → create dependency', {
                predecessor: formatGanttTaskIdForLog(ld.fromId),
                successor: formatGanttTaskIdForLog(toId),
                linkType: 'FS',
                fromLinkEnd: ld.linkEnd,
              });
            }
            void onCreateFsDependency(ld.fromId, toId, 'FS');
          } else {
            if (isGanttScheduleDebugEnabled()) {
              ganttScheduleDebugLog('link drag drop → create dependency', {
                predecessor: formatGanttTaskIdForLog(toId),
                successor: formatGanttTaskIdForLog(ld.fromId),
                linkType: 'FS',
                fromLinkEnd: ld.linkEnd,
              });
            }
            void onCreateFsDependency(toId, ld.fromId, 'FS');
          }
        } else if (isGanttScheduleDebugEnabled()) {
          const sample = stack
            .slice(0, 12)
            .map((n) =>
              n instanceof HTMLElement
                ? n.className?.toString?.().slice(0, 40) || n.tagName
                : String(n),
            );
          ganttScheduleDebugWarn('link drag mouseup: no dependency created', {
            fromId: ld.fromId,
            linkEnd: ld.linkEnd,
            toId: toId ?? null,
            sameTask: toId === ld.fromId,
            elementsFromPointSample: sample,
          });
        }
        linkDragRef.current = null;
        setLinkDrag(null);
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    },
    [displayMode, hideTooltip, onCreateFsDependency, applyLinkDragAutoScroll],
  );

  const getGroupColor = (task: ProjectTask) => {
    const group = groups.find(g => g.id === task.groupId);
    return group?.color || '#6B7280';
  };

  const getBarColor = (task: ProjectTask) => (
    resolveTaskBarColor ? resolveTaskBarColor(task) : getGroupColor(task)
  );

  const renderTaskTooltip = (task: ProjectTask) => {
    const group = groups.find(g => g.id === task.groupId);
    const assigneeNames = (task.assignedTo || [])
      .map((id) => teamMembers.find((u) => u.uid === id)?.displayName || id)
      .filter(Boolean);
    const phaseLine = phaseDisplay(projectPhase ?? undefined);
    const tS = dragPreview && dragPreview.taskId === task.id ? dragPreview.start : new Date(task.startDate);
    const tE = dragPreview && dragPreview.taskId === task.id ? dragPreview.end : new Date(task.endDate);
    const start = tS.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const end = tE.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const daySpan = taskCalendarDaySpan(tS, tE);
    const initialsStr = taskAssigneeInitials(task, teamMembers);
    return (
      <div>
        <div className="pm-gantt-tooltip-title">{task.title}</div>
        <div className="pm-gantt-tooltip-row">
          <span className="pm-gantt-tooltip-k">Group</span> {group?.name || '—'}
        </div>
        {phaseLine && (
          <div className="pm-gantt-tooltip-row">
            <span className="pm-gantt-tooltip-k">Project phase</span> {phaseLine}
          </div>
        )}
        <div className="pm-gantt-tooltip-row">{start} — {end}</div>
        <div className="pm-gantt-tooltip-row">
          <span className="pm-gantt-tooltip-k">Duration</span> {daySpan} calendar day{daySpan !== 1 ? 's' : ''}
        </div>
        <div className="pm-gantt-tooltip-row">
          <span className="pm-gantt-tooltip-k">Status</span> {fmtEnum(String(task.status))}
          {' · '}
          <span className="pm-gantt-tooltip-k">Priority</span> {fmtEnum(String(task.priority))}
          {' · '}
          <span className="pm-gantt-tooltip-k">Progress</span> {task.progress}%
        </div>
        {assigneeNames.length > 0 && (
          <div className="pm-gantt-tooltip-row">
            <span className="pm-gantt-tooltip-k">Assignees</span> {assigneeNames.join(', ')}
            {initialsStr ? (
              <span className="pm-gantt-tooltip-initials"> ({initialsStr})</span>
            ) : null}
          </div>
        )}
        {task.description?.trim() && (
          <div className="pm-gantt-tooltip-desc">{task.description}</div>
        )}
        {task.department === 'installation' && getInstallTaskRangeRainyDays && (
          (() => {
            const rainy = getInstallTaskRangeRainyDays(tS, tE);
            if (rainy.length === 0) return null;
            const fmt = formatInstallDayWeatherLine ?? ((d: InstallDayWeather) => d.ymd);
            return (
              <div className="pm-gantt-tooltip-weather">
                <div className="pm-gantt-tooltip-k">Weather</div>
                <ul className="pm-gantt-tooltip-weather-list">
                  {rainy.map(day => (
                    <li key={day.ymd}>{fmt(day)}</li>
                  ))}
                </ul>
              </div>
            );
          })()
        )}
      </div>
    );
  };

  const renderMilestoneTooltip = (ms: Milestone) => (
    <div>
      <div className="pm-gantt-tooltip-title">{ms.title}</div>
      <div className="pm-gantt-tooltip-row">
        <span className="pm-gantt-tooltip-k">Target</span>{' '}
        {new Date(ms.targetDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
      </div>
      <div className="pm-gantt-tooltip-row">
        <span className="pm-gantt-tooltip-k">Status</span> {fmtEnum(ms.status)}
      </div>
      {ms.description?.trim() && (
        <div className="pm-gantt-tooltip-desc">{ms.description}</div>
      )}
    </div>
  );

  const renderProposalSignedTooltip = () => (
    <div>
      <div className="pm-gantt-tooltip-title">Proposal signed</div>
      <div className="pm-gantt-tooltip-row">
        {proposalSignedAt!.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
      </div>
      <div className="pm-gantt-tooltip-row pm-gantt-tooltip-hint">
        Milestone when the client signed (contract / engagement).
      </div>
    </div>
  );

  /**
   * Manual critical-path overlay: chain tasks flagged `onCriticalPath` by timeline order
   * (start → end → id). This is visualization only, not schedule-critical (CPM).
   */
  const criticalPathOverlayNodes = useMemo(() => {
    if (!showCriticalPathOverlay) return null;
    const taskRowMap = new Map<string, number>();
    flatRows.forEach((r, i) => {
      if (isGanttTaskFlatRow(r) && r.task.id) taskRowMap.set(r.task.id, i);
    });
    const maxVisibleRow = Math.max(0, flatRows.length - 1);
    const visibleRowIndexForTask = (taskId: string, groupId: string | undefined): number | undefined => {
      const vis = taskRowMap.get(taskId);
      if (vis !== undefined) return vis;
      if (groupId) {
        const gi = flatRows.findIndex((r) => r.type === 'group' && r.group.id === groupId);
        if (gi >= 0) return gi;
      }
      const exp = taskRowIndexExpanded.get(taskId);
      if (exp === undefined) return undefined;
      return Math.min(exp, maxVisibleRow);
    };

    const flagged = tasks.filter((t) => t.onCriticalPath && t.id);
    if (flagged.length < 2) return null;

    const sorted = [...flagged].sort((a, b) => {
      const as = dragPreview && dragPreview.taskId === a.id ? dragPreview.start : new Date(a.startDate);
      const bs = dragPreview && dragPreview.taskId === b.id ? dragPreview.start : new Date(b.startDate);
      const ae = dragPreview && dragPreview.taskId === a.id ? dragPreview.end : new Date(a.endDate);
      const be = dragPreview && dragPreview.taskId === b.id ? dragPreview.end : new Date(b.endDate);
      const c = as.getTime() - bs.getTime();
      if (c !== 0) return c;
      const c2 = ae.getTime() - be.getTime();
      if (c2 !== 0) return c2;
      return String(a.id).localeCompare(String(b.id));
    });

    const out: React.ReactNode[] = [];
    let seg = 0;
    for (let i = 0; i < sorted.length - 1; i++) {
      const pred = sorted[i];
      const succ = sorted[i + 1];
      const predId = pred.id!;
      const succId = succ.id!;
      const predRowIdx = visibleRowIndexForTask(predId, pred.groupId);
      const succRowIdx = visibleRowIndexForTask(succId, succ.groupId);
      if (predRowIdx === undefined || succRowIdx === undefined) continue;

      const dStart = dragPreview && dragPreview.taskId === predId ? dragPreview.start : new Date(pred.startDate);
      const dEnd = dragPreview && dragPreview.taskId === predId ? dragPreview.end : new Date(pred.endDate);
      const sStart = dragPreview && dragPreview.taskId === succId ? dragPreview.start : new Date(succ.startDate);
      const sEnd = dragPreview && dragPreview.taskId === succId ? dragPreview.end : new Date(succ.endDate);

      const predSi = dayIndex(dStart);
      const predEi = dayIndex(dEnd);
      const predLeftX = Math.min(predSi, predEi) * cellWidth;
      const predRightX = (Math.max(predSi, predEi) + 1) * cellWidth;

      const succSi = dayIndex(sStart);
      const succEi = dayIndex(sEnd);
      const succLeftX = Math.min(succSi, succEi) * cellWidth;
      const succRightX = (Math.max(succSi, succEi) + 1) * cellWidth;

      const xPredFinish = ganttBarAttachX(predLeftX, predRightX, 'finish', cellWidth);
      const xSuccStart = ganttBarAttachX(succLeftX, succRightX, 'start', cellWidth);

      const y1 = ganttTaskBarCenterY(predRowIdx);
      const y2 = ganttTaskBarCenterY(succRowIdx);
      const pathD = ganttDependencyConnectorPath(xPredFinish, y1, xSuccStart, y2, 'FS');
      out.push(<path key={`cp-p-${seg}`} d={pathD} className="pm-gantt-critical-path-line" fill="none" />);
      out.push(
        <polygon
          key={`cp-a-${seg}`}
          points={`${xSuccStart},${y2} ${xSuccStart + 7},${y2 - 3.5} ${xSuccStart + 7},${y2 + 3.5}`}
          className="pm-gantt-critical-path-arrowhead"
        />,
      );
      seg += 1;
    }
    return out.length ? out : null;
  }, [showCriticalPathOverlay, tasks, flatRows, dragPreview, dayIndex, cellWidth, taskRowIndexExpanded]);

  const renderDependencyLayers = () => {
    const strip = { visible: [] as React.ReactNode[], hit: [] as React.ReactNode[], labels: [] as React.ReactNode[] };
    const taskLayer = { visible: [] as React.ReactNode[], hit: [] as React.ReactNode[], labels: [] as React.ReactNode[] };
    let depLayerKey = 0;
    let depHitKey = 0;
    const msH = 0;
    const taskRowMap = new Map<string, number>();
    flatRows.forEach((r, i) => {
      if (isGanttTaskFlatRow(r) && r.task.id) taskRowMap.set(r.task.id!, i);
    });

    const maxVisibleRow = Math.max(0, flatRows.length - 1);

    /**
     * Row index in `flatRows` for drawing (must stay within SVG height = flatRows.length × row height).
     * Uses the task row when visible; when its group is collapsed, uses that group’s header row.
     */
    const visibleRowIndexForTask = (taskId: string, groupId: string | undefined): number | undefined => {
      const vis = taskRowMap.get(taskId);
      if (vis !== undefined) return vis;
      if (groupId) {
        const gi = flatRows.findIndex((r) => r.type === 'group' && r.group.id === groupId);
        if (gi >= 0) return gi;
      }
      const exp = taskRowIndexExpanded.get(taskId);
      if (exp === undefined) return undefined;
      return Math.min(exp, maxVisibleRow);
    };

    const resolvePredGeometry = (
      depId: string,
    ): { y1: number; dStart: Date; dEnd: Date; predLeftX: number; predRightX: number } | null => {
      const depTask = tasks.find((t) => t.id === depId);
      if (depTask) {
        const depRowIdx = visibleRowIndexForTask(depId, depTask.groupId);
        if (depRowIdx === undefined) return null;
        const dStart =
          dragPreview && dragPreview.taskId === depId ? dragPreview.start : new Date(depTask.startDate);
        const dEnd = dragPreview && dragPreview.taskId === depId ? dragPreview.end : new Date(depTask.endDate);
        const depStartIdx = dayIndex(dStart);
        const depEndIdx = dayIndex(dEnd);
        const predRightX = (Math.max(depStartIdx, depEndIdx) + 1) * cellWidth;
        const predLeftX = Math.min(depStartIdx, depEndIdx) * cellWidth;
        /** Row-relative Y (task arrow SVG sits in a wrapper below the milestone strip). */
        const y1 = ganttTaskBarCenterY(depRowIdx);
        return { y1, dStart, dEnd, predLeftX, predRightX };
      }
      if (isMilestoneBridgeId(depId)) {
        const predMs = milestones.find((m) => m.id === parseMilestoneBridgeId(depId));
        if (!predMs?.id) return null;
        const dEnd =
          dragPreview && dragPreview.taskId === depId ? dragPreview.start : new Date(predMs.targetDate);
        const dStart = dEnd;
        const depStartIdx = dayIndex(dStart);
        const depEndIdx = dayIndex(dEnd);
        const predRightX = (Math.max(depStartIdx, depEndIdx) + 1) * cellWidth;
        const predLeftX = Math.min(depStartIdx, depEndIdx) * cellWidth;
        const y1 = STRIP_MILESTONE_CY;
        return { y1, dStart, dEnd, predLeftX, predRightX };
      }
      return null;
    };

    const pushHit = (
      pathD: string,
      layer: 'strip' | 'task',
      edgeEmphasized: boolean,
      depId: string,
      succId: string,
      impliedLag: number,
      linkType: DependencyEdgeType,
      storedLagBiz: number,
    ) => {
      if (!onUpdateDependencyLag && !onRemoveDependency) return;
      const hitArr = layer === 'strip' ? strip.hit : taskLayer.hit;
      const hk = depHitKey++;
      const predRevealEnd: 'start' | 'finish' = linkType === 'SS' || linkType === 'SF' ? 'start' : 'finish';
      const zeroLagAria = storedLagBiz === 0 && impliedLag >= 0 ? '; same-day weekday offset (0 stored)' : '';
      hitArr.push(
        <path
          key={`dep-h-${hk}-${layer}`}
          d={pathD}
          className={`pm-gantt-arrow-hit${edgeEmphasized ? '' : ' pm-gantt-arrow-hit--muted'}`}
          aria-label={`Dependency (${linkType}) — lag ${impliedLag} weekdays${zeroLagAria} — click to edit lag`}
          onMouseEnter={() => setDepEdgeReveal({ taskId: depId, end: predRevealEnd })}
          onMouseLeave={(e) => {
            if (isMovingToDependencyOverlay(e.relatedTarget)) return;
            setDepEdgeReveal(null);
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (isGanttScheduleDebugEnabled()) {
              ganttScheduleDebugLog('dependency hit (open lag popover)', {
                predecessorId: depId,
                successorId: succId,
                linkType,
                impliedLagWeekdays: impliedLag,
                layer,
              });
            }
            setLagPopover({
              successorId: succId,
              predecessorId: depId,
              lagDraft: String(impliedLag),
              linkType,
            });
          }}
        />,
      );
    };

    const pushEdge = (
      depId: string,
      succId: string,
      succStart: Date,
      y2Content: number,
      succEndXOverride?: number,
      edgeType: DependencyEdgeType = 'FS',
      lagBusinessDays = 0,
    ) => {
      const storedLagBiz = Math.max(-500, Math.min(500, Math.floor(Number(lagBusinessDays) || 0)));
      const pred = resolvePredGeometry(depId);
      if (!pred) {
        const skipKey = `${depId}|${succId}|${edgeType}`;
        if (!warnedSkippedDepEdgeRef.current.has(skipKey)) {
          warnedSkippedDepEdgeRef.current.add(skipKey);
          ganttScheduleDebugWarn(
            'dependency line skipped (predecessor not in timeline `tasks`, row hidden, or unknown id) — engine may still enforce this edge',
            { predecessorId: depId, successorId: succId, edgeType },
          );
        }
        return;
      }
      const edgeEmphasized = depEdgeMatchesReveal(depId, succId, edgeType, depEdgeReveal);
      const { y1: y1Content, dStart, dEnd, predLeftX, predRightX } = pred;
      const predM = isMilestoneBridgeId(depId);
      const succM = isMilestoneBridgeId(succId);
      const targetStartIdx = dayIndex(succStart);

      let succLeftX = targetStartIdx * cellWidth;
      let succRightX = (targetStartIdx + 1) * cellWidth;
      /** Canonical successor dates — must match row bar geometry (`dragPreview` + task Doc). */
      let succLagRefStart = succStart;
      let succLagRefEnd = succStart;
      if (!succM) {
        const succTask = tasks.find((t) => t.id === succId);
        if (succTask?.id) {
          const sStart =
            dragPreview && dragPreview.taskId === succId ? dragPreview.start : new Date(succTask.startDate);
          const sEnd = dragPreview && dragPreview.taskId === succId ? dragPreview.end : new Date(succTask.endDate);
          succLagRefStart = sStart;
          succLagRefEnd = sEnd;
          const si = dayIndex(sStart);
          const ei = dayIndex(sEnd);
          succLeftX = Math.min(si, ei) * cellWidth;
          succRightX = (Math.max(si, ei) + 1) * cellWidth;
        }
      }

      const successorUsesFinish = edgeType === 'FF' || edgeType === 'SF';

      const predAnchStart = ganttBarAttachX(predLeftX, predRightX, 'start', cellWidth);
      const predAnchFinish = ganttBarAttachX(predLeftX, predRightX, 'finish', cellWidth);
      const predAnchorX = edgeType === 'SS' || edgeType === 'SF' ? predAnchStart : predAnchFinish;

      /** Milestone predecessors: spline starts at diamond horizontal center; tasks use inset anchors. */
      const predBridgeMs = predM ? milestones.find((m) => m.id === parseMilestoneBridgeId(depId)) : undefined;
      const predSplineX =
        predM && predBridgeMs?.id ? milestoneAnchorX(predBridgeMs) : predAnchorX;

      const succAnchStart = ganttBarAttachX(succLeftX, succRightX, 'start', cellWidth);
      const succAnchFinish = ganttBarAttachX(succLeftX, succRightX, 'finish', cellWidth);

      /** X where the spline meets the successor task/milestone anchor (inside rounded bar edges). */
      const succAttachX =
        succEndXOverride !== undefined
          ? succEndXOverride
          : successorUsesFinish
            ? succAnchFinish
            : succAnchStart;

      let rawImpliedLag: number;
      switch (edgeType) {
        case 'SS':
          rawImpliedLag = inferSsLagBusinessDays(dStart, succLagRefStart);
          break;
        case 'FF':
          rawImpliedLag = inferFfLagBusinessDays(dEnd, succLagRefEnd);
          break;
        case 'SF':
          rawImpliedLag = inferSfLagBusinessDays(dStart, succLagRefEnd);
          break;
        default:
          rawImpliedLag = inferFsLagBusinessDays(dEnd, succLagRefStart);
          break;
      }
      const impliedLag = Math.max(-500, Math.min(500, rawImpliedLag));
      const mutedClass = edgeEmphasized ? '' : ' pm-gantt-arrow--muted';
      const headMutedClass = edgeEmphasized ? '' : ' pm-gantt-arrowhead--muted';
      const negLagClass = impliedLag < 0 ? ' pm-gantt-arrow--negative-lag' : '';
      const headNegLagClass = impliedLag < 0 ? ' pm-gantt-arrowhead--negative-lag' : '';
      /** Stored lag zero = “no offset between anchor events” → highlight as intentional same‑day chaining. */
      const zeroLagClass =
        impliedLag >= 0 && storedLagBiz === 0 ? ' pm-gantt-arrow--zero-lag' : '';
      const headZeroLagClass =
        impliedLag >= 0 && storedLagBiz === 0 ? ' pm-gantt-arrowhead--zero-lag' : '';
      const msHintZeroClass =
        impliedLag >= 0 && storedLagBiz === 0 ? ' pm-gantt-milestone-dep-hint--zero-lag' : '';

      /** Teal styling + centered “Same Day” along Bézier spline (skipped on vertical-only stubs). */
      const showSameDayLabel = zeroLagClass.length > 0;

      const appendDepStroke = (
        layer: 'strip' | 'task',
        pathD: string,
        keyFragment: string,
        strokeClassNames: string,
        skipSameDayLabel: boolean,
      ): void => {
        const vis = layer === 'strip' ? strip.visible : taskLayer.visible;
        const labArr = layer === 'strip' ? strip.labels : taskLayer.labels;
        const lk = depLayerKey++;
        const k = `${keyFragment}-${lk}`;
        const pathUid = `pm-gantt-sdcurve-${lk}`;
        const wantsLabel = showSameDayLabel && !skipSameDayLabel;
        if (wantsLabel) {
          vis.push(<path key={k} d={pathD} className={strokeClassNames} />);
          labArr.push(
            <g key={`${k}-sdl`}>
              <defs>
                <path id={pathUid} d={pathD} />
              </defs>
              <text
                className={`pm-gantt-dep-same-day-text${
                  edgeEmphasized ? ' pm-gantt-dep-same-day-text--revealed' : ' pm-gantt-dep-same-day-text--idle'
                }`}
                pointerEvents="none"
              >
                <textPath href={`#${pathUid}`} startOffset="50%" textAnchor="middle">
                  Same Day
                </textPath>
              </text>
            </g>,
          );
        } else {
          vis.push(<path key={k} d={pathD} className={strokeClassNames} />);
        }
      };

      const attachDepArrowEnd = (
        layer: 'strip' | 'task',
        arrow: { x: number; y: number; toFinish?: boolean } | null,
      ): void => {
        if (!arrow) return;
        const vis = layer === 'strip' ? strip.visible : taskLayer.visible;
        if (arrow.toFinish) addArrowToFinishAnchor(vis, arrow.x, arrow.y);
        else addArrowToStartAnchor(vis, arrow.x, arrow.y);
      };

      const emit = (
        pathD: string,
        layer: 'strip' | 'task',
        arrow: { x: number; y: number; toFinish?: boolean } | null,
        opts?: { skipSameDayLabel?: boolean },
      ) => {
        const arrowLineClass = `pm-gantt-arrow${mutedClass}${negLagClass}${zeroLagClass}`;
        appendDepStroke(layer, pathD, 'em', arrowLineClass, opts?.skipSameDayLabel ?? false);
        attachDepArrowEnd(layer, arrow);
        pushHit(pathD, layer, edgeEmphasized, depId, succId, impliedLag, edgeType, storedLagBiz);
      };

      const addArrowToStartAnchor = (vis: React.ReactNode[], tipX: number, tipY: number) => {
        const layerK = depLayerKey++;
        vis.push(
          <polygon
            key={`arr-v-${layerK}`}
            points={`${tipX},${tipY} ${tipX + 7},${tipY - 3.5} ${tipX + 7},${tipY + 3.5}`}
            className={`pm-gantt-arrowhead${headMutedClass}${headNegLagClass}${headZeroLagClass}`}
          />,
        );
      };

      const addArrowToFinishAnchor = (vis: React.ReactNode[], tipX: number, tipY: number) => {
        const layerK = depLayerKey++;
        vis.push(
          <polygon
            key={`arr-vf-${layerK}`}
            points={`${tipX},${tipY} ${tipX - 7},${tipY - 3.5} ${tipX - 7},${tipY + 3.5}`}
            className={`pm-gantt-arrowhead${headMutedClass}${headNegLagClass}${headZeroLagClass}`}
          />,
        );
      };

      /** Arrowhead pointing up (toward milestone strip) at end of short task→milestone stem. */
      const addArrowUp = (vis: React.ReactNode[], tipX: number, baseY: number) => {
        const layerK = depLayerKey++;
        const tipY = baseY - 7;
        vis.push(
          <polygon
            key={`arr-up-${layerK}`}
            points={`${tipX},${tipY} ${tipX - 4},${baseY} ${tipX + 4},${baseY}`}
            className={`pm-gantt-arrowhead${headMutedClass}${headNegLagClass}${headZeroLagClass}`}
          />,
        );
      };

      if (msH === 0) {
        const pathD = ganttDependencyConnectorPath(
          predAnchorX,
          y1Content,
          succAttachX,
          y2Content,
          edgeType,
        );
        emit(pathD, 'task', { x: succAttachX, y: y2Content, toFinish: successorUsesFinish });
        return;
      }

      if (!predM && !succM) {
        const pathD = ganttDependencyConnectorPath(predAnchorX, y1Content, succAttachX, y2Content, edgeType);
        emit(pathD, 'task', { x: succAttachX, y: y2Content, toFinish: successorUsesFinish });
        return;
      }

      if (predM && succM) {
        const pathD = ganttDependencyConnectorPath(
          predSplineX,
          STRIP_MILESTONE_CY,
          succAttachX,
          STRIP_MILESTONE_CY,
          edgeType,
        );
        emit(pathD, 'strip', { x: succAttachX, y: STRIP_MILESTONE_CY, toFinish: successorUsesFinish });
        return;
      }

      if (predM && !succM) {
        const pathStrip = ganttDependencyConnectorPath(
          predSplineX,
          STRIP_MILESTONE_CY,
          succAttachX,
          msH,
          edgeType,
        );
        const y2p = y2Content;
        const pathTask = ganttDependencyConnectorPath(succAttachX, 0, succAttachX, y2p, edgeType);
        const arrowLineClass = `pm-gantt-arrow${mutedClass}${negLagClass}${zeroLagClass}`;
        appendDepStroke('strip', pathStrip, 'ps-s', arrowLineClass, false);
        pushHit(pathStrip, 'strip', edgeEmphasized, depId, succId, impliedLag, edgeType, storedLagBiz);
        emit(pathTask, 'task', { x: succAttachX, y: y2p, toFinish: successorUsesFinish }, { skipSameDayLabel: true });
        return;
      }

      /** Task → milestone (FS): vertical chain from task finish up to strip, then to diamond — no bend when lag is 0. */
      if (!predM && succM && edgeType === 'FS' && impliedLag === 0) {
        const succMs = milestones.find((m) => m.id && milestoneBridgeId(m.id) === succId);
        const ax = succMs ? milestoneAnchorX(succMs) : succAttachX;
        const predTask = tasks.find((t) => t.id === depId);
        const depRowIdx = predTask ? visibleRowIndexForTask(depId, predTask.groupId) : undefined;
        if (depRowIdx !== undefined && msH > 0) {
          const yRowTop = depRowIdx * ROW_HEIGHT;
          const xFinish = predAnchFinish;
          const yM = STRIP_MILESTONE_CY;
          const vertClass = `pm-gantt-arrow pm-gantt-arrow--task-milestone${mutedClass}${negLagClass}${zeroLagClass}`;

          const pathTaskVert = `M ${xFinish} ${y1Content} L ${xFinish} ${yRowTop}`;
          appendDepStroke('task', pathTaskVert, 'tmv-t', vertClass, true);
          pushHit(pathTaskVert, 'task', edgeEmphasized, depId, succId, impliedLag, edgeType, storedLagBiz);

          const stripBottom = msH;
          let pathStripTm: string;
          if (Math.abs(xFinish - ax) < 0.75) {
            pathStripTm = `M ${xFinish} ${stripBottom} L ${xFinish} ${yM}`;
          } else {
            pathStripTm = `M ${xFinish} ${stripBottom} L ${xFinish} ${yM} L ${ax} ${yM}`;
          }
          const stripVertOnly = Math.abs(xFinish - ax) < 0.75;
          appendDepStroke('strip', pathStripTm, 'tmv-s', vertClass, stripVertOnly);
          pushHit(pathStripTm, 'strip', edgeEmphasized, depId, succId, impliedLag, edgeType, storedLagBiz);

          const arrowBaseY = yM + 8;
          addArrowUp(strip.visible, ax, arrowBaseY);
          return;
        }
      }

      {
        const succMs = milestones.find((m) => m.id && milestoneBridgeId(m.id) === succId);
        const ax = succMs ? milestoneAnchorX(succMs) : succAttachX;
        const taskBarTopY = y1Content - ROW_HEIGHT / 2;
        const stemEndY = Math.max(6, taskBarTopY - MILESTONE_TASK_DEP_STEM_GAP_PX);
        const pathTask = ganttDependencyConnectorPath(predAnchorX, y1Content, ax, stemEndY, edgeType);
        const arrowLineClass = `pm-gantt-arrow${mutedClass}${negLagClass}${zeroLagClass}`;
        appendDepStroke('task', pathTask, 'ms-br', arrowLineClass, false);
        addArrowUp(taskLayer.visible, ax, stemEndY);
        pushHit(pathTask, 'task', edgeEmphasized, depId, succId, impliedLag, edgeType, storedLagBiz);
        const hintCy = STRIP_MILESTONE_CY + MILESTONE_DEP_HINT_BELOW_CENTER_PX;
        const layerKs = depLayerKey++;
        strip.visible.push(
          <circle
            key={`dep-ms-hint-${layerKs}`}
            cx={ax}
            cy={hintCy}
            r={MILESTONE_DEP_HINT_R}
            className={`pm-gantt-milestone-dep-hint${edgeEmphasized ? '' : ' pm-gantt-milestone-dep-hint--muted'}${impliedLag < 0 ? ' pm-gantt-milestone-dep-hint--negative-lag' : ''}${msHintZeroClass}`}
          />,
        );
      }
    };

    tasks.forEach((task) => {
      const edges = getDependencyEdges(task);
      if (!edges.length) return;
      const targetRowIdx = taskRowMap.get(task.id!);
      if (targetRowIdx === undefined) return;
      const tStart = dragPreview && dragPreview.taskId === task.id ? dragPreview.start : new Date(task.startDate);
      const succRowForY = visibleRowIndexForTask(task.id!, task.groupId) ?? targetRowIdx;
      const y2 = ganttTaskBarCenterY(succRowForY);
      const succId = task.id!;

      edges.forEach((edge) => {
        pushEdge(edge.predecessorId, succId, tStart, y2, undefined, edge.type, edge.lagBusinessDays);
      });
    });

    milestones.forEach((ms) => {
      const edges = getMilestoneDependencyEdges(ms);
      if (!edges.length || !ms.id) return;
      const succId = milestoneBridgeId(ms.id);
      const tStart =
        dragPreview && dragPreview.taskId === succId ? dragPreview.start : new Date(ms.targetDate);
      const y2 = STRIP_MILESTONE_CY;
      const ax = milestoneAnchorX(ms);
      edges.forEach((edge) => {
        pushEdge(edge.predecessorId, succId, tStart, y2, ax, edge.type, edge.lagBusinessDays);
      });
    });

    return { strip, taskLayer };
  };

  const depLayers = renderDependencyLayers();

  const renderPinButton = (row: GanttFlatRow) => {
    if (!allProjectsMode || displayMode) return null;
    const key = ganttFlatRowPinKey(row);
    if (!key) return null;
    const pinned = pinKeySet.has(key);
    const blocked = !pinned && pinKeys.length >= GANTT_COMPARE_PIN_LIMIT;
    return (
      <button
        type="button"
        className={`pm-gantt-pin-btn${pinned ? ' is-on' : ''}`}
        aria-pressed={pinned}
        aria-label={pinned ? 'Unpin from compare strip' : 'Pin to compare strip'}
        title={
          pinned
            ? 'Unpin from compare strip'
            : blocked
              ? `Pin limit reached (${GANTT_COMPARE_PIN_LIMIT})`
              : 'Pin to compare at top'
        }
        disabled={blocked}
        onClick={(e) => {
          e.stopPropagation();
          togglePinKey(key);
        }}
      >
        <Pin size={11} strokeWidth={2.25} aria-hidden />
      </button>
    );
  };

  const conflictBadgeForRow = (row: GanttFlatRow, label: string) => {
    if (!allProjectsMode || displayMode || !conflictBoard) return null;
    const issues = row.type === 'section'
      ? collapsedSections.has(row.section.id)
        ? issuesForGanttRow(conflictBoard, conflictFamily, { sectionId: row.section.id })
        : null
      : row.type === 'group'
        ? row.group.id && collapsedGroups.has(row.group.id)
          ? issuesForGanttRow(conflictBoard, conflictFamily, {
            groupId: row.group.id,
            groupName: row.group.name,
            groupProjectId: row.group.projectId,
          })
          : null
        : row.type === 'task'
          ? issuesForGanttTaskRow(conflictBoard, conflictFamily, {
            taskId: unwrapSupplierDisplayTaskId(row.task.id) || row.task.id,
            parentExpanded: !!(row.task.id && row.childCount > 0 && expandedParentIds.has(row.task.id)),
          })
          : row.type === 'subtask'
            ? issuesForGanttTaskRow(conflictBoard, conflictFamily, {
              taskId: unwrapSupplierDisplayTaskId(row.task.id) || row.task.id,
              parentTaskId: row.parentId,
            })
            : null;
    if (!issues || !hasConflictIssues(issues)) return null;
    const toneScope = row.type === 'section'
      ? (() => {
        const projectId = parseGroupTimelineProjectSectionId(row.section.id);
        return projectId ? { projectId } : undefined;
      })()
      : row.type === 'group' && row.group.projectId
        ? { projectId: row.group.projectId }
        : row.type === 'task' || row.type === 'subtask'
          ? { taskId: unwrapSupplierDisplayTaskId(row.task.id) || row.task.id }
          : undefined;
    return (
      <GroupTimelineConflictBadge
        issues={issues}
        label={label}
        toneScope={toneScope}
        onShowConflicts={onConflictBadgeClick}
      />
    );
  };

  const renderLeftRow = (row: GanttFlatRow, keyPrefix: string) => {
    if (row.type === 'section') {
      const isCollapsed = collapsedSections.has(row.section.id);
      const sectionProjectId = parseGroupTimelineProjectSectionId(row.section.id);
      const simUc = isSimulatedProjectRow(sectionProjectId);
      return (
        <div
          key={`${keyPrefix}-s-${row.section.id}`}
          className={`pm-gantt-left-section${sectionProjectId ? ' pm-gantt-left-section--project' : ''}${simUc ? ' pm-gantt-sim-uc' : ''}`}
          style={{ height: ROW_HEIGHT }}
        >
          <div
            className="pm-gantt-left-section-color"
            style={{ background: row.section.color }}
          />
          <button
            type="button"
            className="pm-gantt-left-toggle"
            aria-label={isCollapsed ? `Expand ${row.section.name}` : `Collapse ${row.section.name}`}
            onClick={() => collapseSection(row.section.id)}
          >
            {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
          </button>
          <span className="pm-gantt-left-section-name">{row.section.name}</span>
          {conflictBadgeForRow(row, row.section.name)}
          {simUc && multiProjectTimeline && (
            <span className="pm-gantt-sim-uc-flask" title="Simulated under contract for planning">
              <FlaskConical size={11} strokeWidth={2.25} aria-hidden />
            </span>
          )}
          {renderPinButton(row)}
          <span className="pm-gantt-left-count">{row.taskCount}</span>
        </div>
      );
    }
    if (row.type === 'group') {
      const isCollapsed = !!(row.group.id && collapsedGroups.has(row.group.id));
      const simUc = isSimulatedProjectRow(row.group.projectId);
      return (
        <div
          key={`${keyPrefix}-g-${row.group.id}`}
          className={`pm-gantt-left-group${simUc ? ' pm-gantt-sim-uc' : ''}`}
          style={{ height: ROW_HEIGHT }}
        >
          <div className="pm-gantt-left-group-color" style={{ background: row.group.color }} />
          <button
            type="button"
            className="pm-gantt-left-toggle"
            aria-label={isCollapsed ? `Expand ${row.group.name}` : `Collapse ${row.group.name}`}
            onClick={() => {
              if (row.group.id) collapseGroup(row.group.id);
            }}
          >
            {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
          </button>
          <span className="pm-gantt-left-group-name">{row.group.name}</span>
          {conflictBadgeForRow(row, row.group.name)}
          {simUc && multiProjectTimeline && !projectSectionTimeline && (
            <span className="pm-gantt-sim-uc-flask" title="Simulated under contract for planning">
              <FlaskConical size={11} strokeWidth={2.25} aria-hidden />
            </span>
          )}
          {renderPinButton(row)}
          <span className="pm-gantt-left-count">{row.taskCount}</span>
        </div>
      );
    }
    return (
      <div
        key={`${keyPrefix}-t-${row.task.id}`}
        className={`pm-gantt-left-task ${row.type === 'subtask' ? 'pm-gantt-left-task--subtask' : ''}${isSimulatedProjectRow(row.task.projectId) ? ' pm-gantt-sim-uc' : ''}${statusMenuTaskId === row.task.id ? ' pm-dropdown-host--open' : ''}`}
        style={{ height: ROW_HEIGHT }}
        onClick={() => onSelectTask(row.task)}
      >
        {row.type === 'subtask' ? (
          <span className="pm-gantt-left-subtask-indent" aria-hidden />
        ) : row.type === 'task' && row.childCount > 0 ? (
          <button
            type="button"
            className="pm-gantt-left-task-expand"
            aria-label={expandedParentIds.has(row.task.id!) ? 'Collapse subtasks' : 'Expand subtasks'}
            onClick={(e) => {
              e.stopPropagation();
              const pid = row.task.id!;
              setExpandedParentIds((prev) => {
                const next = new Set(prev);
                if (next.has(pid)) next.delete(pid);
                else next.add(pid);
                return next;
              });
            }}
          >
            {expandedParentIds.has(row.task.id!) ? (
              <ChevronDown size={13} />
            ) : (
              <ChevronRight size={13} />
            )}
          </button>
        ) : null}
        <span
          className={`pm-gantt-left-task-name ${row.type === 'task' && row.childCount > 0 ? 'pm-gantt-left-task-name--summary' : ''}`.trim()}
        >
          {row.task.title}
        </span>
        {conflictBadgeForRow(row, row.task.title)}
        {(() => {
          const pill = formatSubtaskStructurePill(row.task);
          if (!pill) return null;
          return (
            <span className="pm-gantt-left-structure-badge">
              {pill}
            </span>
          );
        })()}
        {renderPinButton(row)}
        <div
          className={`pm-gantt-left-task-status${statusMenuTaskId === row.task.id ? ' pm-dropdown-host--open' : ''}`}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <StatusPill
            status={row.task.status}
            readonly={statusReadOnly}
            onChange={statusReadOnly ? undefined : (status) => {
              // Status column is on the right of the left panel; keep clicks off the
              // row select handler and persist via the same path as table StatusPill.
              onUpdateTask(row.task.id!, {
                status,
                progress: status === 'completed' ? 100 : row.task.progress,
              });
            }}
            onOpenChange={(open) => {
              setStatusMenuTaskId(open && row.task.id ? row.task.id : null);
            }}
          />
        </div>
      </div>
    );
  };

  const dragDateOverride = dragPreview
    ? { taskId: dragPreview.taskId, startDate: dragPreview.start, endDate: dragPreview.end }
    : null;

  const renderSummaryRollupBar = (
    name: string,
    color: string,
    taskCount: number,
    rollup: { startDate: Date; endDate: Date },
    onToggle: () => void,
  ) => {
    const startIdx = dayIndex(rollup.startDate);
    const endIdx = dayIndex(rollup.endDate);
    const barLeft = startIdx * cellWidth;
    const barWidth = Math.max((endIdx - startIdx + 1) * cellWidth, cellWidth);
    const daySpanCol = Math.max(1, endIdx - startIdx + 1);
    const countLabel = `${taskCount} task${taskCount === 1 ? '' : 's'}`;
    return (
      <div
        className="pm-gantt-bar pm-gantt-bar--summary pm-gantt-bar--readonly pm-gantt-bar--group-rollup"
        style={{
          left: barLeft,
          width: barWidth,
          top: GANTT_BAR_TOP_PAD,
          height: GANTT_BAR_BODY_HEIGHT,
          ['--pm-gantt-bar-color' as string]: color,
        }}
        title={`${name} · ${countLabel} · ${daySpanCol}d`}
        onClick={onToggle}
      >
        <span className="pm-gantt-bar-summary-body" aria-hidden>
          <span className="pm-gantt-bar-summary-base" />
          <span className="pm-gantt-bar-summary-edge pm-gantt-bar-summary-edge--start" />
          <span className="pm-gantt-bar-summary-edge pm-gantt-bar-summary-edge--end" />
        </span>
        <div className="pm-gantt-bar-label-row">
          {barWidth > 60 && (
            <span className="pm-gantt-bar-label">{name}</span>
          )}
          <span className="pm-gantt-bar-meta">{countLabel} · {daySpanCol}d</span>
        </div>
      </div>
    );
  };

  const renderSectionRightRow = (row: Extract<GanttFlatRow, { type: 'section' }>, keyPrefix: string) => {
    const isCollapsed = collapsedSections.has(row.section.id);
    const sectionProjectId = parseGroupTimelineProjectSectionId(row.section.id);
    const parents = row.section.groupIds.flatMap((id) => tasksByGroup.get(id) ?? []);
    const children = parents.flatMap((p) => (p.id ? childTasksByParent.get(p.id) ?? [] : []));
    const rollup = isCollapsed
      ? rollupGroupTaskDates(parents, children, dragDateOverride)
      : null;
    return (
      <div
        key={`${keyPrefix}-sr-${row.section.id}`}
        className={`pm-gantt-row pm-gantt-row-section${isSimulatedProjectRow(sectionProjectId) ? ' pm-gantt-sim-uc' : ''}`}
        style={{ height: ROW_HEIGHT }}
      >
        {rollup
          ? renderSummaryRollupBar(
            row.section.name,
            row.section.color || '#64748b',
            row.taskCount,
            rollup,
            () => collapseSection(row.section.id),
          )
          : null}
      </div>
    );
  };

  const renderGroupRightRow = (row: Extract<GanttFlatRow, { type: 'group' }>, keyPrefix: string) => {
    const isCollapsed = !!(row.group.id && collapsedGroups.has(row.group.id));
    const parents = row.group.id ? (tasksByGroup.get(row.group.id) ?? []) : [];
    const children = parents.flatMap((p) => (p.id ? childTasksByParent.get(p.id) ?? [] : []));
    const rollup = isCollapsed
      ? rollupGroupTaskDates(parents, children, dragDateOverride)
      : null;
    return (
      <div
        key={`${keyPrefix}-gr-${row.group.id}`}
        className={`pm-gantt-row pm-gantt-row-group${isSimulatedProjectRow(row.group.projectId) ? ' pm-gantt-sim-uc' : ''}`}
        style={{ height: ROW_HEIGHT }}
      >
        {rollup
          ? renderSummaryRollupBar(
            row.group.name,
            row.group.color || '#64748b',
            row.taskCount,
            rollup,
            () => {
              if (row.group.id) collapseGroup(row.group.id);
            },
          )
          : null}
      </div>
    );
  };

  const renderPinnedTaskBar = (row: Extract<GanttFlatRow, { type: 'task' | 'subtask' }>, keyPrefix: string) => {
    const task = row.task;
    const summaryChildren = task.id ? childTasksByParent.get(task.id) ?? [] : [];
    const rolledUpDates = row.type === 'task' && row.childCount > 0
      ? rollupDatesFromChildren(summaryChildren)
      : null;
    const tStart = rolledUpDates ? rolledUpDates.startDate : new Date(task.startDate);
    const tEnd = rolledUpDates ? rolledUpDates.endDate : new Date(task.endDate);
    const startIdx = dayIndex(tStart);
    const endIdx = dayIndex(tEnd);
    const barLeft = startIdx * cellWidth;
    const barWidth = Math.max((endIdx - startIdx + 1) * cellWidth, cellWidth);
    const color = getBarColor(task);
    const isCompleted = task.status === 'completed';
    return (
      <div
        key={`${keyPrefix}-tr-${task.id}`}
        className="pm-gantt-row"
        style={{ height: ROW_HEIGHT }}
      >
        <div
          className={`pm-gantt-bar ${isCompleted ? 'pm-gantt-bar--completed' : ''} ${resolveTaskBarColor ? 'pm-gantt-bar--custom-color' : ''}`}
          style={{
            left: barLeft,
            width: barWidth,
            top: GANTT_BAR_TOP_PAD,
            height: GANTT_BAR_BODY_HEIGHT,
            ['--pm-gantt-bar-color' as string]: color,
            ...(!isCompleted || resolveTaskBarColor ? { background: color } : {}),
          }}
          title={task.title}
          onClick={() => onSelectTask(task)}
        >
          <div className="pm-gantt-bar-progress" style={{ width: `${task.progress}%` }} />
          {barWidth > 60 && (
            <div className="pm-gantt-bar-label-row">
              <span className="pm-gantt-bar-label">{task.title}</span>
            </div>
          )}
        </div>
        {barWidth <= 60 && task.title?.trim() && (
          <div
            className={`pm-gantt-bar-external-label ${isCompleted ? 'pm-gantt-bar-external-label--completed' : ''}`}
            style={{ left: barLeft + barWidth + 6, opacity: 0.92 }}
            title={task.title}
          >
            <span className="pm-gantt-bar-external-label-text">{task.title}</span>
          </div>
        )}
      </div>
    );
  };

  const renderPinnedRightRow = (row: GanttFlatRow) => {
    if (row.type === 'section') return renderSectionRightRow(row, 'pin');
    if (row.type === 'group') return renderGroupRightRow(row, 'pin');
    return renderPinnedTaskBar(row, 'pin');
  };

  return (
    <div
      ref={ganttRootRef}
      className={[
        'pm-gantt',
        hideLeftPanel ? 'pm-gantt--no-left' : '',
        displayMode ? 'pm-gantt--display' : '',
        resizingLeftPanel ? 'pm-gantt--resizing-left' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-gantt-link-dragging={linkDrag ? 'true' : undefined}
    >
      {/* Zoom controls */}
      <div className="pm-gantt-controls">
        {!displayMode && onTimelineUndo && (
          <button
            type="button"
            className="pm-btn pm-btn-sm pm-gantt-undo-btn"
            disabled={!timelineUndoCanUndo || timelineUndoBusy}
            onClick={() => void onTimelineUndo()}
            title={`Undo last schedule change (${undoShortcutHint})`}
            aria-label="Undo last schedule change"
          >
            <Undo2 size={16} strokeWidth={2.25} aria-hidden />
            <span className="pm-gantt-undo-btn-label">Undo</span>
          </button>
        )}
        <div className="pm-gantt-controls-zoom">
          <button type="button" className={`pm-btn pm-btn-sm ${zoom === 'month' ? 'active' : ''}`} onClick={() => { externalColumnLockRef.current = false; setZoom('month'); }}>Month</button>
          <button type="button" className={`pm-btn pm-btn-sm ${zoom === 'week' ? 'active' : ''}`} onClick={() => { externalColumnLockRef.current = false; setZoom('week'); }}>Week</button>
          <button type="button" className={`pm-btn pm-btn-sm ${zoom === 'day' ? 'active' : ''}`} onClick={() => { externalColumnLockRef.current = false; setZoom('day'); }}>Day</button>
          <button
            type="button"
            className={`pm-btn pm-btn-sm ${zoom === 'fit' ? 'active' : ''}`}
            onClick={handleZoomToFit}
            title="Fit the full date range to the timeline width (updates when the panel is resized)"
          >
            Fit
          </button>
          <button
            type="button"
            className="pm-btn pm-btn-sm pm-gantt-fullscreen-btn"
            onClick={handleToggleGanttFullscreen}
            title={fullscreenActive ? 'Exit fullscreen' : 'Fullscreen timeline'}
            aria-pressed={fullscreenActive}
          >
            {fullscreenActive ? <Minimize2 size={16} strokeWidth={2.25} aria-hidden /> : <Maximize2 size={16} strokeWidth={2.25} aria-hidden />}
            <span className="pm-gantt-fullscreen-btn-label">{fullscreenActive ? 'Exit' : 'Fullscreen'}</span>
          </button>
        </div>
      </div>

      <div className="pm-gantt-container" ref={ganttContainerRef}>
        {ganttTimelinePresence ? (
          <GanttTimelinePresenceLayer
            selfViewHash={ganttTimelinePresence.selfViewHash}
            selfSyncId={ganttTimelinePresence.selfSyncId}
            peers={ganttTimelinePresence.peers}
            projection={presenceProjection}
          />
        ) : null}
        {!hideLeftPanel && (
          <div className="pm-gantt-left" style={{ width: leftPanelWidth }}>
            <button
              type="button"
              className="pm-gantt-left-resize"
              aria-label="Resize task list"
              onPointerDown={beginLeftPanelResize}
            />
            <div className="pm-gantt-left-header">
              <div className="pm-gantt-left-header-task">
                <span>Task</span>
                {onStructureFilterChange && dealStructures.length > 0 && (
                  <GanttStructureFilter
                    structures={dealStructures}
                    filter={structureFilter}
                    onChange={onStructureFilterChange}
                  />
                )}
              </div>
              <span className="pm-gantt-left-header-status">Status</span>
            </div>
            {allProjectsMode && pinnedRows.length > 0 && (
              <div className="pm-gantt-pin-left" role="region" aria-label="Pinned compare rows">
                {pinnedRows.map((row) => renderLeftRow(row, 'pin'))}
              </div>
            )}
            <div
              className="pm-gantt-left-body pm-scroll"
              ref={leftBodyScrollRef}
              onScroll={handleLeftBodyScroll}
              onPointerMove={ganttTimelinePresence ? (e) => handleGanttPresenceMove(e, 'left') : undefined}
              onPointerLeave={ganttTimelinePresence ? handleGanttPresenceLeave : undefined}
            >
              {flatRows.map((row) => renderLeftRow(row, 'scroll'))}
            </div>
          </div>
        )}

        {/* Right panel — timeline */}
        <div className="pm-gantt-right">
          {/* Timeline header */}
          <div className="pm-gantt-header" ref={headerScrollRef}>
            <div style={{ width: totalWidth, minWidth: totalWidth }}>
              {/* Month row */}
              <div className="pm-gantt-months">
                {months.map((m, i) => {
                  const segmentW = m.days * cellWidth;
                  const d0 = days[m.startIdx];
                  const longLabel = d0.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  const label =
                    segmentW < 76
                      ? d0.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
                      : longLabel;
                  return (
                    <div
                      key={i}
                      className="pm-gantt-month"
                      style={{ width: segmentW, left: m.startIdx * cellWidth }}
                      title={d0.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
              {/* Day row */}
              <div className="pm-gantt-days">
                {days.map((d, i) => {
                  const dow = d.getDay();
                  const isWeekend = dow === 0 || dow === 6;
                  const isToday = i === todayIdx;
                  const weekendBookend =
                    dow === 6 ? 'pm-gantt-day--weekend-start' : dow === 0 ? 'pm-gantt-day--weekend-end' : '';
                  const dayPayments = paymentFlagsByDayIndex.get(i);
                  const paymentDay = !!dayPayments?.length;
                  const dayMeetings = meetingsByDayKey.get(dayKeyFromDate(d));
                  const meetingDay = !!dayMeetings?.length;
                  return (
                    <div
                      key={i}
                      className={`pm-gantt-day ${isWeekend ? 'weekend' : ''} ${weekendBookend} ${isToday ? 'today' : ''} ${paymentDay ? 'pm-gantt-day--payment' : ''} ${meetingDay ? 'pm-gantt-day--meeting' : ''}`}
                      style={{ width: cellWidth }}
                    >
                      {paymentDay ? (
                        <GanttPaymentDayCell
                          items={dayPayments!}
                          compact={cellWidth < 28}
                          badge={meetingDay ? <GanttMeetingFlagBadge meetings={dayMeetings!} /> : null}
                          extraTip={meetingDay ? <MeetingTipContent meetings={dayMeetings!} /> : null}
                        />
                      ) : meetingDay ? (
                        <GanttMeetingDayCell meetings={dayMeetings!} compact={cellWidth < 28} />
                      ) : (
                        showDayNumbersInHeader && d.getDate()
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {allProjectsMode && pinnedRows.length > 0 && (
            <div
              className="pm-gantt-pin-right"
              ref={pinRightScrollRef}
              role="region"
              aria-label="Pinned compare timeline"
            >
              <div
                className="pm-gantt-pin-right-inner"
                style={{
                  width: totalWidth,
                  minWidth: totalWidth,
                  height: pinnedRows.length * ROW_HEIGHT,
                  position: 'relative',
                }}
              >
                {todayIdx >= 0 && (
                  <div
                    className="pm-gantt-today-line"
                    style={{ left: todayIdx * cellWidth + cellWidth / 2, height: pinnedRows.length * ROW_HEIGHT }}
                  />
                )}
                {pinnedRows.map((row) => renderPinnedRightRow(row))}
              </div>
            </div>
          )}

          {/* Timeline body */}
          <div
            className="pm-gantt-body pm-scroll"
            ref={scrollRef}
            onScroll={handleTimelineScroll}
            onPointerMove={ganttTimelinePresence ? (e) => handleGanttPresenceMove(e, 'chart') : undefined}
            onPointerLeave={ganttTimelinePresence ? handleGanttPresenceLeave : undefined}
          >
            <div style={{ width: totalWidth, minWidth: totalWidth, position: 'relative' }}>
              {/* Weekend columns */}
              {days.map((d, i) => {
                const dow = d.getDay();
                const isWeekend = dow === 0 || dow === 6;
                if (!isWeekend) return null;
                const bookend =
                  dow === 6 ? 'pm-gantt-weekend-col--start' : dow === 0 ? 'pm-gantt-weekend-col--end' : '';
                return (
                  <div
                    key={`bg-${i}`}
                    className={`pm-gantt-weekend-col ${bookend}`}
                    style={{ left: i * cellWidth, width: cellWidth, height: timelineContentHeight }}
                  />
                );
              })}

              {/* Today line */}
              {todayIdx >= 0 && (
                <div
                  className="pm-gantt-today-line"
                  style={{ left: todayIdx * cellWidth + cellWidth / 2, height: timelineContentHeight }}
                />
              )}

              {/* Task rows + dependency SVG share one coordinate system (row-relative Y). */}
              <div className="pm-gantt-task-rows-wrap" style={{ position: 'relative', width: '100%' }}>
                <svg
                  className="pm-gantt-arrows pm-gantt-arrows--overlay pm-gantt-arrows--tasks"
                  style={{
                    width: totalWidth,
                    height: flatRows.length * ROW_HEIGHT,
                    top: 0,
                    left: 0,
                  }}
                >
                  {depLayers.taskLayer.visible}
                </svg>
              {/* Rows */}
              {flatRows.map((row) => {
                if (row.type === 'section') return renderSectionRightRow(row, 'scroll');
                if (row.type === 'group') return renderGroupRightRow(row, 'scroll');

                const task = row.task;
                const isSubtaskRow = row.type === 'subtask';
                const parentChildCount = row.type === 'task' ? row.childCount : 0;
                const hasSubtasks = parentChildCount > 0;
                const summaryChildren = task.id ? childTasksByParent.get(task.id) ?? [] : [];
                const rolledUpDates = hasSubtasks ? rollupDatesFromChildren(summaryChildren) : null;
                const tStart = dragPreview && dragPreview.taskId === task.id
                  ? dragPreview.start
                  : rolledUpDates
                    ? rolledUpDates.startDate
                    : new Date(task.startDate);
                const tEnd = dragPreview && dragPreview.taskId === task.id
                  ? dragPreview.end
                  : rolledUpDates
                    ? rolledUpDates.endDate
                    : new Date(task.endDate);
                const startIdx = dayIndex(tStart);
                const endIdx = dayIndex(tEnd);
                const barLeft = startIdx * cellWidth;
                const barWidth = Math.max((endIdx - startIdx + 1) * cellWidth, cellWidth);
                const color = getBarColor(task);
                const ob = isOnboardingBridgeTaskId(task.id!);
                const isCompleted = task.status === 'completed';
                const isLocked = isTaskLocked(task);
                const isSummaryTask = hasSubtasks && !isCompleted;
                /** Summary tasks roll up from subtasks — not draggable on the timeline. */
                const timelineDatesLocked =
                  (ob && !onCommitTaskDates) || isLocked || isSummaryTask;
                const daySpanCol = Math.max(1, endIdx - startIdx + 1);
                const assigneeInitials = taskAssigneeInitials(task, teamMembers);
                const showOpenBarMeta =
                  !isCompleted &&
                  daySpanCol >= GANTT_OPEN_META_MIN_DAY_SPAN &&
                  barWidth >= GANTT_OPEN_META_MIN_BAR_WIDTH;
                const openBarMetaParts = [
                  assigneeInitials,
                  hasSubtasks ? `${parentChildCount} subtask${parentChildCount === 1 ? '' : 's'}` : '',
                  `${daySpanCol}d`,
                ].filter(Boolean);
                const openBarMetaText = openBarMetaParts.join(' · ');
                const revealStart = depEdgeReveal?.taskId === task.id && depEdgeReveal?.end === 'start';
                const revealFinish = depEdgeReveal?.taskId === task.id && depEdgeReveal?.end === 'finish';
                const showTitleInsideBar = barWidth > 60;
                const showTitleOutsideBar = !showTitleInsideBar && !!task.title?.trim();
                const canShowLockToggle =
                  !!onToggleTaskLock && !isCompleted && !displayMode && !isSummaryTask;
                const lockBadgeText = isLocked
                  ? isCompleted
                    ? 'Completed (locked to actuals). Edit completion to change dates.'
                    : 'Locked. Click the lock icon to unlock.'
                  : 'Click to lock this task to its current dates.';
                const installRainyDays =
                  task.department === 'installation' && getInstallTaskRangeRainyDays
                    ? getInstallTaskRangeRainyDays(tStart, tEnd)
                    : [];
                const showInstallRain = installRainyDays.length > 0;
                const installRainTitle = showInstallRain
                  ? installRainyDays
                      .map(d => (formatInstallDayWeatherLine ? formatInstallDayWeatherLine(d) : d.ymd))
                      .join('\n')
                  : undefined;
                const barTop = GANTT_BAR_TOP_PAD;
                const barHeight = GANTT_BAR_BODY_HEIGHT;

                const barLabelContent = (showTitleInsideBar || showOpenBarMeta) ? (
                  <div className="pm-gantt-bar-label-row">
                    {showTitleInsideBar && (
                      <span className="pm-gantt-bar-label">{task.title}</span>
                    )}
                    {showOpenBarMeta && openBarMetaText && (
                      <span className="pm-gantt-bar-meta">{openBarMetaText}</span>
                    )}
                  </div>
                ) : null;

                const barInteraction = !timelineDatesLocked ? (
                  <div className="pm-gantt-bar-interaction" aria-hidden>
                    <div
                      className="pm-gantt-bar-resize pm-gantt-bar-resize--start"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleMouseDown(e, task.id!, 'resize-start');
                      }}
                    />
                    <div
                      role="presentation"
                      className="pm-gantt-bar-move"
                      style={canShowLockToggle ? { marginRight: 28 } : undefined}
                      title="Drag to move bar on the timeline. Release with Shift held to ripple to dependents without prompting."
                      onMouseDown={(e) => handleMouseDown(e, task.id!, 'move')}
                      onClick={() => {
                        if (Date.now() < suppressSelectUntilRef.current) return;
                        onSelectTask(task);
                      }}
                    />
                    <div
                      className="pm-gantt-bar-resize pm-gantt-bar-resize--end"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleMouseDown(e, task.id!, 'resize-end');
                      }}
                    />
                  </div>
                ) : null;

                const barChrome = (
                  <>
                    {canShowLockToggle && (
                      <button
                        type="button"
                        className={`pm-gantt-bar-lock ${isLocked ? 'is-on' : ''}`}
                        aria-pressed={isLocked}
                        aria-label={isLocked ? 'Unlock this task' : 'Lock this task'}
                        title={lockBadgeText}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          void onToggleTaskLock!(task.id!, !isLocked);
                        }}
                      >
                        {isLocked ? <Lock size={11} aria-hidden /> : <Unlock size={11} aria-hidden />}
                      </button>
                    )}
                    {showInstallRain && (
                      <InstallWeatherRainIcon
                        className={`pm-gantt-bar-weather-rain${canShowLockToggle ? ' pm-gantt-bar-weather-rain--with-lock' : ''}`}
                        title={installRainTitle}
                        size={11}
                      />
                    )}
                    {onCreateFsDependency && (
                      <>
                        <button
                          type="button"
                          className="pm-gantt-bar-link pm-gantt-bar-link--start"
                          data-gantt-link-handle="1"
                          data-gantt-link-end="start"
                          aria-label={`Start anchor for “${task.title}”: drag backward to a predecessor’s finish on a finish-to-start link, or drop another task’s Finish port here.`}
                          title="Start — drag backward to a predecessor (finish-to-start)"
                          onMouseDown={(e) => handleLinkMouseDown(e, task.id!, 'start')}
                        >
                          <span className="pm-gantt-bar-link-badge" aria-hidden>
                            Start
                          </span>
                        </button>
                        <button
                          type="button"
                          className="pm-gantt-bar-link pm-gantt-bar-link--finish"
                          data-gantt-link-handle="1"
                          data-gantt-link-end="finish"
                          aria-label={`Finish anchor for “${task.title}”: drag to another task to create finish-to-start (their start follows your finish).`}
                          title="Finish — drag forward to successor (finish-to-start)"
                          onMouseDown={(e) => handleLinkMouseDown(e, task.id!, 'finish')}
                        >
                          <span className="pm-gantt-bar-link-badge" aria-hidden>
                            Finish
                          </span>
                        </button>
                      </>
                    )}
                  </>
                );

                const rowTaskId = unwrapSupplierDisplayTaskId(task.id) || task.id;
                const taskGhosts = timelineDriftGhosts.filter(
                  (g) => g.taskId === task.id || g.taskId === rowTaskId,
                );
                return (
                  <div
                    key={`tr-${task.id}`}
                    className={`pm-gantt-row${isSimulatedProjectRow(task.projectId) ? ' pm-gantt-sim-uc' : ''}`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    {taskGhosts.map((ghost, gi) => {
                      const gStart = dayIndex(ghost.start);
                      const gEnd = dayIndex(ghost.end);
                      const fill = `${Math.round(18 + ((ghost.opacity - 0.14) / 0.28) * 16)}%`;
                      return (
                        <div
                          key={`drift-${task.id}-${gi}`}
                          className={`pm-gantt-drift-ghost pm-gantt-drift-ghost--${ghost.cause}`}
                          style={{
                            left: gStart * cellWidth,
                            width: Math.max((gEnd - gStart + 1) * cellWidth, cellWidth),
                            top: barTop,
                            height: barHeight,
                            ['--pm-drift-fill' as string]: fill,
                          }}
                          title={TIMELINE_DRIFT_CAUSE_LABELS[ghost.cause]}
                          aria-hidden
                        />
                      );
                    })}
                    <div
                      className={`pm-gantt-bar ${isSummaryTask ? 'pm-gantt-bar--summary' : ''} ${dragging?.taskId === task.id ? 'dragging' : ''} ${timelineDatesLocked ? 'pm-gantt-bar--readonly' : ''} ${isCompleted ? 'pm-gantt-bar--completed' : ''} ${isLocked && !isCompleted ? 'pm-gantt-bar--locked' : ''} ${revealStart ? 'pm-gantt-bar--reveal-start' : ''} ${revealFinish ? 'pm-gantt-bar--reveal-finish' : ''} ${resolveTaskBarColor ? 'pm-gantt-bar--custom-color' : ''}`}
                      style={{
                        left: barLeft,
                        width: barWidth,
                        top: barTop,
                        height: barHeight,
                        ['--pm-gantt-bar-color' as string]: color,
                        ...(!isSummaryTask && (!isCompleted || resolveTaskBarColor) ? { background: color } : {}),
                        cursor: timelineDatesLocked ? 'default' : undefined,
                      }}
                      data-gantt-task-id={task.id}
                      onClick={
                        timelineDatesLocked
                          ? () => {
                              if (Date.now() < suppressSelectUntilRef.current) return;
                              onSelectTask(task);
                            }
                          : undefined
                      }
                      onMouseEnter={(e) => {
                        const onLink = (e.target as HTMLElement).closest('.pm-gantt-bar-link');
                        armTaskTooltipAfterLinkRef.current = !!onLink;
                        if (!onLink) {
                          scheduleTooltip(e.clientX, e.clientY, renderTaskTooltip(task));
                        }
                      }}
                      onMouseMove={(e) => {
                        updateDepEdgeRevealFromBarPointer(e, task.id!);
                        const onLink = (e.target as HTMLElement).closest('.pm-gantt-bar-link');
                        if (onLink) {
                          hideTooltip();
                          armTaskTooltipAfterLinkRef.current = true;
                        } else {
                          if (armTaskTooltipAfterLinkRef.current) {
                            scheduleTooltip(e.clientX, e.clientY, renderTaskTooltip(task));
                          }
                          armTaskTooltipAfterLinkRef.current = false;
                        }
                      }}
                      onMouseLeave={(e) => {
                        armTaskTooltipAfterLinkRef.current = false;
                        hideTooltip();
                        if (!isMovingToDependencyOverlay(e.relatedTarget)) setDepEdgeReveal(null);
                      }}
                    >
                      {isSummaryTask && (
                        <span className="pm-gantt-bar-summary-body" aria-hidden>
                          <span className="pm-gantt-bar-summary-base" />
                          <span className="pm-gantt-bar-summary-edge pm-gantt-bar-summary-edge--start" />
                          <span className="pm-gantt-bar-summary-edge pm-gantt-bar-summary-edge--end" />
                        </span>
                      )}
                      <div className="pm-gantt-bar-progress" style={{ width: `${task.progress}%` }} />
                      {barLabelContent}
                      {barInteraction}
                      {isSubtaskRow && (
                        <sup className="pm-gantt-bar-sub-badge" aria-label="Subtask">
                          Sub
                        </sup>
                      )}
                      {!isSummaryTask && barChrome}
                    </div>
                    {showTitleOutsideBar && (
                      <div
                        className={`pm-gantt-bar-external-label ${isCompleted ? 'pm-gantt-bar-external-label--completed' : ''}`}
                        style={{ left: barLeft + barWidth + 6 }}
                        title={task.title}
                      >
                        <span className="pm-gantt-bar-external-label-text">{task.title}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {criticalPathOverlayNodes && criticalPathOverlayNodes.length > 0 && (
                <svg
                  className="pm-gantt-critical-path pm-gantt-arrows pm-gantt-arrows--overlay"
                  style={{
                    width: totalWidth,
                    height: flatRows.length * ROW_HEIGHT,
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                  }}
                  aria-hidden
                >
                  {criticalPathOverlayNodes}
                </svg>
              )}
              {(onUpdateDependencyLag || onRemoveDependency) && depLayers.taskLayer.hit.length > 0 && (
                <svg
                  className="pm-gantt-arrows pm-gantt-arrows--overlay pm-gantt-arrows--tasks-hit"
                  style={{
                    width: totalWidth,
                    height: flatRows.length * ROW_HEIGHT,
                    top: 0,
                    left: 0,
                  }}
                  aria-hidden
                >
                  {depLayers.taskLayer.hit}
                </svg>
              )}
              {depLayers.taskLayer.labels.length > 0 && (
                <svg
                  className="pm-gantt-arrows pm-gantt-arrows--overlay pm-gantt-arrows--tasks-same-day-labels"
                  style={{
                    width: totalWidth,
                    height: flatRows.length * ROW_HEIGHT,
                    top: 0,
                    left: 0,
                  }}
                  aria-hidden
                >
                  {depLayers.taskLayer.labels}
                </svg>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {tooltip && createPortal(
        <div
          ref={tooltipRef}
          className="pm-gantt-tooltip"
          style={{ left: tooltip.x + GANTT_TOOLTIP_CURSOR_OFFSET, top: tooltip.y + GANTT_TOOLTIP_CURSOR_OFFSET }}
          role="tooltip"
        >
          {tooltip.content}
        </div>,
        document.body,
      )}

      {linkDrag &&
        createPortal(
          <>
            <svg className="pm-gantt-link-rubber" aria-hidden>
              <path d={ganttDependencyConnectorPath(linkDrag.x, linkDrag.y, linkDrag.curX, linkDrag.curY, 'FS')} />
            </svg>
            <div
              className="pm-gantt-link-drag-hud"
              style={{ left: linkDrag.curX + 18, top: linkDrag.curY + 18 }}
              role="status"
              aria-live="polite"
            >
              <div className="pm-gantt-link-drag-hud-kicker">
                {linkDrag.linkEnd === 'finish' ? 'From finish' : 'From start'}
              </div>
              <div className="pm-gantt-link-drag-hud-title">
                {ganttLinkSourceLabel(linkDrag.fromId, tasks, milestones)}
              </div>
              <div className="pm-gantt-link-drag-hud-detail">
                {linkDrag.linkEnd === 'finish'
                  ? 'Drag to another task — new link is finish-to-start (pred finish → succ start)'
                  : 'Drag backward to another task — new link is finish-to-start via this task’s start'}
              </div>
            </div>
          </>,
          document.body,
        )}

      {lagPopover && (onUpdateDependencyLag || onRemoveDependency) && createPortal(
        <GanttLagPopover
          state={lagPopover}
          popoverRef={lagPopoverRef}
          predecessor={lagEndpointTaskShim(lagPopover.predecessorId, tasks, milestones)}
          successor={lagEndpointTaskShim(lagPopover.successorId, tasks, milestones)}
          allowEditLag={!!onUpdateDependencyLag}
          allowRemove={!!onRemoveDependency}
          onLinkTypeChange={(next) =>
            setLagPopover((p) => {
              if (!p) return null;
              const pred = lagEndpointTaskShim(p.predecessorId, tasks, milestones);
              const succ = lagEndpointTaskShim(p.successorId, tasks, milestones);
              if (!pred?.id || !succ?.id) return { ...p, linkType: next };
              const newLag = impliedLagForNewDependencyEdge(pred, succ, next);
              return { ...p, linkType: next, lagDraft: String(newLag) };
            })
          }
          onLagDraftChange={(draft) => setLagPopover((prev) => (prev ? { ...prev, lagDraft: draft } : null))}
          onDismiss={() => setLagPopover(null)}
          onApply={() => {
            if (!onUpdateDependencyLag) return;
            const v = parseInt(lagPopover.lagDraft, 10);
            if (Number.isNaN(v)) return;
            if (isGanttScheduleDebugEnabled()) {
              ganttScheduleDebugLog('lag popover apply', {
                successorId: lagPopover.successorId,
                predecessorId: lagPopover.predecessorId,
                linkType: lagPopover.linkType,
                lagBusinessDays: v,
              });
            }
            void onUpdateDependencyLag(lagPopover.successorId, lagPopover.predecessorId, {
              lagBusinessDays: v,
              type: lagPopover.linkType,
            });
            setLagPopover(null);
          }}
          onRemove={
            onRemoveDependency
              ? () => {
                  if (isGanttScheduleDebugEnabled()) {
                    ganttScheduleDebugLog('lag popover remove dependency', {
                      successorId: lagPopover.successorId,
                      predecessorId: lagPopover.predecessorId,
                      linkType: lagPopover.linkType,
                    });
                  }
                  void onRemoveDependency(lagPopover.successorId, lagPopover.predecessorId);
                  setLagPopover(null);
                }
              : undefined
          }
        />,
        document.body,
      )}
    </div>
  );
};

export default GanttTimeline;
